// REQUIRED IMPORTS — do not remove: MoveEntryDialog, ArrowsLeftRight, sortPaymentModes, loadParties
// (loadParties is INTENTIONALLY absent — parties refresh via reloadLedger();
//  reintroducing a `loadParties()` reference crashes the whole page. If you
//  must add another helper here, extend reloadLedger's implementation instead.)
// See /app/REGRESSION_CHECKLIST.md · Invariant #1 for the full list.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import useCanDelete from "@/lib/useCanDelete";
import { dmY, dmYhm } from "@/lib/dateFmt";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EmptyState from "@/components/EmptyState";
import LedgerNotifyBell from "@/components/LedgerNotifyBell";
import { History, FileText, FileSpreadsheet, ChevronDown, Download, Clock } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import AuditTrailDialog from "@/components/AuditTrailDialog";
import ShareLedgerButton from "@/components/ShareLedgerButton";
import AmountInput from "@/components/AmountInput";
import { Percent, PencilSimple, Trash, FloppyDisk, Warning, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SortHeader, { sortRows } from "@/components/SortHeader";
import { balanceCls, balanceLabel } from "@/lib/balanceColor";
import JumpToLatestButton from "@/components/JumpToLatestButton";
import useLedgerSortPref from "@/hooks/useLedgerSortPref";
import TxnDateCell from "@/components/TxnDateCell";
import { ArrowsLeftRight } from "@phosphor-icons/react";
import MoveEntryDialog from "@/components/MoveEntryDialog";
import { sortPaymentModes } from "@/lib/paymentModes";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

const TXN_LABELS = {
    invoice: "Invoice",
    payment: "Payment Received",
    commission: "Commission",
    payout: "Payment Made",
    adjustment: "Adjustment",
};

/**
 * v1.28.97 — Searchable Party picker for the Party Ledger page.
 * Replaces the plain shadcn Select so users can type-ahead (Ctrl+L →
 * cursor lands here → first letters filter instantly → Enter picks the
 * top match and loads their ledger). Exposes an `open()` handle via
 * `autoFocusRef` so the parent can trigger it from a global shortcut
 * event without prop-drilling / mounting a duplicate hidden input.
 */
function PartyCombobox({ parties, value, onChange, placeholder, autoFocusRef }) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const inputRef = React.useRef(null);
    const arrowedRef = React.useRef(false); // v1.29.61 — true once the user arrow-navigates the list
    const selected = parties.find((p) => p.id === value);
    React.useEffect(() => {
        if (!autoFocusRef) return;
        autoFocusRef.current = {
            open: () => {
                arrowedRef.current = false;
                setOpen(true);
                setQuery("");
                // The Popover has an enter animation; wait one paint before
                // stealing focus, otherwise the input isn't in the DOM yet.
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => inputRef.current?.focus?.());
                });
            },
        };
        return () => { if (autoFocusRef.current) autoFocusRef.current = null; };
    }, [autoFocusRef]);
    const q = query.trim().toLowerCase();
    const filtered = q
        ? parties.filter((p) => (p.name || "").toLowerCase().includes(q))
        : parties;
    return (
        <Popover open={open} onOpenChange={(o) => { arrowedRef.current = false; setOpen(o); if (o) requestAnimationFrame(() => inputRef.current?.focus?.()); else setQuery(""); }}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="report-party-select"
                    className="inline-flex h-9 w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 text-left text-sm font-medium text-slate-800 shadow-sm transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                    <span className={`truncate ${selected ? "" : "text-slate-400"}`}>
                        {selected?.name || placeholder}
                    </span>
                    <CaretDown size={12} weight="bold" className="shrink-0 text-slate-400" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput
                        ref={inputRef}
                        data-testid="report-party-search"
                        placeholder="Type party name…"
                        value={query}
                        onValueChange={(v) => { arrowedRef.current = false; setQuery(v); }}
                        onKeyDown={(e) => {
                            // v1.29.61 — Keep Enter in sync with the visual
                            // arrow-key highlight. Track when the user moves the
                            // highlight with Arrow keys; if they have, defer Enter
                            // entirely to cmdk so it selects the CURRENTLY
                            // highlighted item. Previously the handler below always
                            // re-picked the top typed-match on Enter, so after
                            // arrow-navigating the party ABOVE the highlight was
                            // selected (the reported off-by-one).
                            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                                arrowedRef.current = true;
                                return; // let cmdk move + own the highlight
                            }
                            // v1.29.09 — Blind-type + Enter (no arrow nav): pick
                            // the best typed match, preferring names that START
                            // with the query over ones that merely contain it,
                            // read fresh from the DOM so the last keystroke isn't
                            // missed. Skipped once the user has arrow-navigated.
                            if (e.key === "Enter") {
                                if (arrowedRef.current) return;
                                const q = (e.currentTarget.value || "").trim().toLowerCase();
                                if (!q) return;
                                e.preventDefault();
                                const list = parties;
                                const starts = list.filter((p) => (p.name || "").toLowerCase().startsWith(q));
                                const contains = starts.length ? starts : list.filter((p) => (p.name || "").toLowerCase().includes(q));
                                if (contains.length > 0) {
                                    onChange(contains[0].id);
                                    setOpen(false);
                                    setQuery("");
                                }
                            }
                        }}
                    />
                    <CommandList>
                        <CommandEmpty>No matching party.</CommandEmpty>
                        <CommandGroup>
                            {filtered.map((p) => (
                                <CommandItem
                                    key={p.id}
                                    value={p.name}
                                    data-testid={`report-party-opt-${p.id}`}
                                    onSelect={() => {
                                        onChange(p.id);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                >
                                    {p.name}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

// v1.29.32 — Default ledger date window: FROM = 7 days ago, TO = today
// (local). Pure module-level helper so both the URL-init effect and the party
// picker can use it without hook-dependency noise.
function defaultWeekRange() {
    const toD = new Date();
    const fromD = new Date();
    fromD.setDate(fromD.getDate() - 7);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: iso(fromD), to: iso(toD) };
}

// v1.29.72 — Persist the last-used Payment Mode filter PER PARTY in
// localStorage so reopening a party's ledger restores their chosen mode.
// ALL is treated as "no preference" (key removed) to keep the map small.
const MODE_FILTER_LS_KEY = "micm.ledgerModeFilter.v1";
function loadModeMap() {
    try { return JSON.parse(localStorage.getItem(MODE_FILTER_LS_KEY) || "{}") || {}; }
    catch { return {}; }
}
function getSavedMode(partyId) {
    if (!partyId) return "ALL";
    return loadModeMap()[partyId] || "ALL";
}
function saveMode(partyId, mode) {
    if (!partyId) return;
    try {
        const map = loadModeMap();
        if (!mode || mode === "ALL") delete map[partyId];
        else map[partyId] = mode;
        localStorage.setItem(MODE_FILTER_LS_KEY, JSON.stringify(map));
    } catch { /* non-fatal */ }
}

// v1.29.60 — Pure ledger-row grouping (1 UTR = 1 row) shared by the on-screen
// table memo AND the pre-download row-count verification, so both always agree
// with the exported PDF/Excel (which uses _group_ledger_snapshot_rows).
function buildDisplayRows(ledger, dateFrom, dateTo, nowMs, recentWindowMs) {
    if (!ledger) return [];
    const isRecent = (r) => {
        const ts = r?.updated_at || r?.created_at;
        if (!ts) return false;
        const t = new Date(ts).getTime();
        if (Number.isNaN(t)) return false;
        return nowMs - t <= recentWindowMs;
    };
    const refKey = (r) => (r?.reference || "").trim().toLowerCase() || null;
    const paymentUploadIds = new Set();
    const paymentRefs = new Set();
    ledger.rows.forEach((r) => {
        if (r.txn_type !== "payment") return;
        if (r.upload_id) paymentUploadIds.add(r.upload_id);
        if (r.id) paymentUploadIds.add(r.id);
        const rk = refKey(r);
        if (rk) paymentRefs.add(rk);
    });
    const commByUpload = new Map();
    const feeByUpload = new Map();
    const commByRef = new Map();
    const feeByRef = new Map();
    ledger.rows.forEach((r) => {
        const uid = r.payment_txn_id || r.upload_id;
        const rk = refKey(r);
        if (r.txn_type === "commission") {
            if (uid) commByUpload.set(uid, r);
            if (rk && !commByRef.has(rk)) commByRef.set(rk, r);
        } else if (r.txn_type === "portal_fee") {
            if (uid) feeByUpload.set(uid, r);
            if (rk && !feeByRef.has(rk)) feeByRef.set(rk, r);
        }
    });

    const out = [];
    const opening = Number(ledger.opening_balance || 0);
    let bal = opening;
    for (const r of ledger.rows) {
        if (dateFrom && r.date < dateFrom) continue;
        if (dateTo && r.date > dateTo) continue;

        if (r.txn_type === "commission" || r.txn_type === "portal_fee") {
            const uid = r.upload_id || r.payment_txn_id;
            const rk = refKey(r);
            const hasParent = (uid && paymentUploadIds.has(uid)) || (rk && paymentRefs.has(rk));
            if (hasParent) continue;
            const amt = Number(r.amount || 0);
            bal += Number(r.amount_signed || 0);
            const isFee = r.txn_type === "portal_fee";
            out.push({
                id: r.id, date: r.date,
                utr: r.reference || (r.description || "").slice(0, 30) || "",
                upload_id: r.upload_id || null,
                recd: 0, pct: 0,
                pctAmt: isFee ? 0 : amt,
                netAmt: 0,
                fixAmt: isFee ? amt : 0,
                paid: 0,
                balance: bal,
                remark: isFee ? "PORTAL FEE" : "COMMISSION",
                notes: r.description || "",
                payment_mode: "",
                _type: r.txn_type,
                _rawAmount: amt,
                _isRecent: isRecent(r),
                _updatedAt: r.updated_at || r.created_at || null,
                created_at: r.created_at || null,
                updated_at: r.updated_at || null,
                reconciled: !!r.reconciled,
                reconciled_by: r.reconciled_by || "",
                reconciled_at: r.reconciled_at || "",
                ocr_mismatch: !!r.ocr_mismatch,
                ocr_mismatch_fields: r.ocr_mismatch_fields || [],
            });
            continue;
        }

        bal += Number(r.amount_signed || 0);
        let recd = 0, pct = 0, pctAmt = 0, fixAmt = 0, netAmt = 0, paid = 0;
        let remark = "";

        if (r.txn_type === "payment") {
            recd = Number(r.amount || 0);
            const rk = refKey(r);
            const linkedComm = commByUpload.get(r.id)
                || commByUpload.get(r.upload_id)
                || (rk ? commByRef.get(rk) : null);
            const linkedFee = feeByUpload.get(r.id)
                || feeByUpload.get(r.upload_id)
                || (rk ? feeByRef.get(rk) : null);
            if (linkedComm) {
                bal += Number(linkedComm.amount_signed || 0);
                pct = Number(linkedComm.charge_percent || 0);
                pctAmt = Number(linkedComm.amount || 0);
            }
            if (linkedFee) {
                bal += Number(linkedFee.amount_signed || 0);
                fixAmt = Number(linkedFee.amount || 0);
            }
            if (linkedComm && !linkedFee && (linkedComm.is_flat_fee || (linkedComm.description || "").startsWith("Portal charge"))) {
                fixAmt = pctAmt;
                pctAmt = 0;
                pct = 0;
            }
            netAmt = recd - pctAmt;
            remark = "APPROVED";
        } else if (r.txn_type === "payout") {
            paid = Number(r.amount || 0);
            remark = r.reference ? r.reference.toUpperCase() : "PAID";
        } else if (r.is_balance_transfer || r.payment_mode === "Balance Transfer") {
            if (r.transfer_direction === "out") paid = Number(r.amount || 0);
            else recd = Number(r.amount || 0);
            remark = "BALANCE TRANSFER";
        } else if (r.txn_type === "invoice") {
            recd = Number(r.amount || 0);
            remark = "INVOICE";
        } else if (r.txn_type === "mediator_commission") {
            pct = Number(r.charge_percent || 0);
            paid = Number(r.amount || 0);
            remark = "COMMISSION";
        } else {
            recd = Number(r.amount || 0);
            remark = String(r.txn_type || "").toUpperCase();
        }

        out.push({
            id: r.id, date: r.date,
            utr: r.utr || r.reference || "",
            upload_id: r.upload_id || null,
            recd, pct, pctAmt, netAmt, fixAmt, paid, balance: bal,
            remark, notes: r.description || "", payment_mode: r.payment_mode || "",
            _type: r.txn_type,
            _commReceipts: Number(r.commission_receipts || r.month_total || 0),
            _commAmount: Number(r.commission_amount || r.amount || 0),
            _commLabel: r.commission_period_label || "",
            _isTransfer: !!(r.is_balance_transfer || r.payment_mode === "Balance Transfer"),
            _transferDir: r.transfer_direction || "",
            _transferRefId: r.transfer_ref_id || "",
            _transferRemarks: r.remarks || r.description || "",
            _rawAmount: Number(r.amount || 0),
            _isRecent: isRecent(r),
            _updatedAt: r.updated_at || r.created_at || null,
            created_at: r.created_at || null,
            updated_at: r.updated_at || null,
            reconciled: !!r.reconciled,
            reconciled_by: r.reconciled_by || "",
            reconciled_at: r.reconciled_at || "",
            ocr_mismatch: !!r.ocr_mismatch,
            ocr_mismatch_fields: r.ocr_mismatch_fields || [],
        });
    }
    return out;
}

export default function Reports() {
    const canDelete = useCanDelete();
    const [parties, setParties] = useState([]);
    const [partyId, setPartyId] = useState("");
    const [editTxnError, setEditTxnError] = useState("");
    // v1.28.97 — Handle to programmatically open + focus the Party combobox
    // when the Ctrl+L (open-ledger) shortcut fires. Set by <PartyCombobox>
    // in a useEffect so the parent can call `.open()` without prop-drilling.
    const partyPickerAutoFocusRef = useRef(null);
    const [ledger, setLedger] = useState(null);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    // v1.29.13 — Ledger sort persists in localStorage so a user's chosen
    // order (newest ↔ oldest first) survives reloads and follows them into
    // the Customer portal ledger too. Default is newest first.
    const [sort, setSort] = useLedgerSortPref({ by: "date", dir: "asc" });
    // Refs used by the "Latest" floating button + first-load auto-scroll.
    const tableTopRef = useRef(null);
    const tableBottomRef = useRef(null);
    const didAutoScrollRef = useRef({});
    const [editTxn, setEditTxn] = useState(null); // { id, amount, date, payment_mode }
    const [editTransfer, setEditTransfer] = useState(null); // v1.29.43 — { refId, amount, date, remarks }
    const [editTransferError, setEditTransferError] = useState("");
    // v1.29.22 — Move Entry dialog. Holds the ledger row currently being
    // reassigned to another party. Uses the existing PATCH /transactions/{id}
    // endpoint (which already rebuilds commission + portal-fee on the
    // destination party's pricing rule and appends a move-audit line to
    // the txn description).
    const [moveTxn, setMoveTxn] = useState(null);
    // v1.29.23 — Bulk-selection for Move-Selected. Set of row ids. Rebuilt
    // as filters change so hidden rows can never leak into a bulk action.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    // v1.29.88 — Move Transactions mode. When OFF (default) the row-select
    // checkboxes + bulk-move bar are hidden; toggling ON reveals them so admin
    // can tick rows and move them to another party.
    const [moveMode, setMoveMode] = useState(false);
    // v1.29.36 — Inline bulk-move toolbar above the table (replaces the old
    // bottom bar + modal): destination party + busy flag.
    const [bulkMoveTarget, setBulkMoveTarget] = useState("");
    const [bulkMoveBusy, setBulkMoveBusy] = useState(false);
    // v1.27.58 — Remember which value in the Rectify dialog's Remarks field
    // came from an auto-fill of a payment-mode preset. Only auto-fill again
    // (on a subsequent mode change) if the current Remarks value is empty
    // OR equals what we last auto-filled — preserves any manual edit and
    // never touches the original saved remark on dialog open.
    const rectifyLastPresetRef = React.useRef("");
    // Segment: "active" (default) or "inactive" — decides which parties populate the dropdown.
    // Inactive parties are otherwise hidden from all dropdowns app-wide.
    const [partySegment, setPartySegment] = useState("active");
    // Reconcile Mode (iter 52) — lets admins tick off each payment/payout row
    // during month-end reconciliation. Unchecked rows visually stand out so
    // discrepancies (e.g. 29 in ledger vs 23 in reality) are easy to spot.
    const [reconcileMode, setReconcileMode] = useState(false);
    const [showOnlyUnchecked, setShowOnlyUnchecked] = useState(false);
    // v1.31.49 — Rows being faded out after being checked in "show unchecked
    // only" mode. They stay in every filtered list until their timer clears
    // them here, giving a ~0.7s fade-out before they drop from view.
    const [fadingRows, setFadingRows] = useState(() => new Set());
    const shouldHideUncheckedRow = (row) =>
        reconcileMode && showOnlyUnchecked
        && (row._type === "payment" || row._type === "payout")
        && row.reconciled && !fadingRows.has(row.id);
    // v1.29.19 — Live UTR + Remark search over the currently-displayed
    // ledger rows. Empty string = no filter. Kept out of the URL / backend
    // params on purpose — the search must feel instant and never trigger a
    // ledger re-fetch, and toggling reconcile checkboxes below must NOT
    // reset this state (the checkbox toggle path calls `reloadLedger()`,
    // which only updates `ledger`; this state lives independently).
    const [rowSearch, setRowSearch] = useState("");
    // v1.29.71 — Payment Mode filter (2nd control row, after search). ALL |
    // CREDIT (Recd>0) | DEBIT (Paid>0) | <specific payment_mode>. Composes with
    // the row search + existing toggles; feeds the count badge, sticky totals
    // and the TOTALS row so they all reflect the filtered set.
    const [modeFilter, setModeFilter] = useState("ALL");
    // v1.29.72 — Holds the mode restored from localStorage on party open so
    // the validation effect can check it against the party's actual modes
    // once the ledger finishes loading (falls back to ALL + toast if gone).
    const pendingModeRef = useRef(null);
    // v1.27.20 — Payment-mode dropdown in Rectify dialog. Loaded once from
    // /payment-modes and refreshed when a new mode is added inline.
    const [paymentModes, setPaymentModes] = useState([]);
    // v1.29.20 — Shared A-Z sort + Title Case + "RECIVED" typo fix for the
    // Rectify Payment modal's Payment Mode dropdown. Any new mode POSTed
    // via the inline "Add new mode" input drops into the right slot on
    // the very next render.
    const sortedPaymentModes = React.useMemo(() => sortPaymentModes(paymentModes), [paymentModes]);
    const [newModeName, setNewModeName] = useState("");
    const loadPaymentModes = React.useCallback(async () => {
        try {
            const { data } = await api.get("/payment-modes");
            const list = data?.modes || data || [];
            setPaymentModes(list);
            // v1.27.61 — debug log so the admin can confirm in DevTools
            // that presets are actually arriving from the API. Prints the
            // full list plus the count of modes that carry a `default_remark`.
            const withPreset = list.filter((m) => (m?.default_remark || "").trim()).length;
            // eslint-disable-next-line no-console
            console.debug(`[MICM] payment-modes loaded · ${list.length} total · ${withPreset} with preset`, list);
        } catch { /* non-fatal */ }
    }, []);
    const addPaymentMode = async () => {
        const name = (newModeName || "").trim();
        if (!name) return;
        try {
            await api.post("/payment-modes", { name });
            toast.success(`Payment mode "${name}" added`);
            setNewModeName("");
            await loadPaymentModes();
            if (editTxn) setEditTxn({ ...editTxn, payment_mode: name });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add mode");
        }
    };
    const navigate = useNavigate();

    const visibleParties = React.useMemo(() =>
        parties.filter((p) => partySegment === "inactive" ? p.active === false : p.active !== false),
    [parties, partySegment]);
    const inactiveCount = React.useMemo(() => parties.filter((p) => p.active === false).length, [parties]);

    const [showSuspense, setShowSuspense] = useState(false);
    // v1.28.26 — Suspense enhancements
    const [suspenseOnly, setSuspenseOnly] = useState(false);
    const [selectedSuspense, setSelectedSuspense] = useState(() => new Set());

    const toggleSuspenseSelect = (id) => {
        setSelectedSuspense((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const bulkUnsuspense = async () => {
        const ids = [...selectedSuspense];
        if (ids.length === 0) return;
        if (!window.confirm(`Un-suspense ${ids.length} payment${ids.length === 1 ? "" : "s"}?\n\nEach will be restored to the closing balance and any linked commission will be reinstated.`)) return;
        try {
            const { data } = await api.post("/transactions/bulk-unsuspense", { ids });
            const okCount = (data?.results || []).filter((r) => r.ok).length;
            toast.success(`Restored ${okCount} of ${ids.length} · cascade=${data?.cascade || 0}`);
            setSelectedSuspense(new Set());
            reloadLedger();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk un-suspense failed");
        }
    };

    const reloadLedger = () => {
        if (!partyId) return;
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (showSuspense) params.include_suspense = "true";
        api.get(`/ledger/${partyId}`, { params }).then((r) => setLedger(r.data));
    };

    // v1.29.47 — Pending slips floating icon + review panel state/handlers.
    // Verifying a slip refetches the ledger (reloadLedger), so pending_uploads
    // shrinks and the badge count + panel list update immediately.
    const [pendingPanelOpen, setPendingPanelOpen] = useState(false);
    const [verifyingId, setVerifyingId] = useState("");
    const [verifyingAll, setVerifyingAll] = useState(false);
    const [rejectingId, setRejectingId] = useState("");
    const [rejectingAll, setRejectingAll] = useState(false);

    const verifyPendingSlip = async (u) => {
        setVerifyingId(u.id);
        try {
            await api.post(`/uploads/${u.id}/verify`, { status: "verified" }, { skipAuthRedirect: true });
            toast.success(`Slip verified · ${fmt(u.amount)}`);
            reloadLedger();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (detail?.message || formatApiError(e)));
        } finally { setVerifyingId(""); }
    };

    const verifyAllPending = async () => {
        const rows = [...(ledger?.pending_uploads || [])];
        if (!rows.length) return;
        setVerifyingAll(true);
        let ok = 0; const fails = [];
        for (const u of rows) {
            try {
                await api.post(`/uploads/${u.id}/verify`, { status: "verified" }, { skipAuthRedirect: true });
                ok += 1;
            } catch (e) {
                const d = e?.response?.data?.detail;
                fails.push(typeof d === "string" ? d : (d?.message || "failed"));
            }
        }
        if (ok) toast.success(`Verified ${ok} slip${ok > 1 ? "s" : ""}`);
        if (fails.length) toast.error(`${fails.length} slip${fails.length > 1 ? "s" : ""} could not be verified — open Upload Slips to review`);
        reloadLedger();
        setVerifyingAll(false);
    };

    // v1.29.89 — Reject a pending slip from the ledger pending-slips panel.
    // Requires a rejection reason (stored as verification_notes). force_upi +
    // force_dup so a reject is never blocked by whitelist / duplicate guards.
    const rejectPendingSlip = async (u) => {
        const reason = window.prompt("Reason for rejecting this slip? (required)");
        if (reason == null) return; // cancelled
        if (!reason.trim()) { toast.error("Rejection reason is required"); return; }
        setRejectingId(u.id);
        try {
            await api.post(`/uploads/${u.id}/verify`, { status: "rejected", notes: reason.trim(), force_upi: true, force_dup: true }, { skipAuthRedirect: true });
            toast.success("Slip rejected", {
                description: `UTR ${u.utr || "—"} · ${fmt(u.amount)}`,
                duration: 8000,
                action: { label: "Undo", onClick: () => restoreRejected(u.id) },
            });
            reloadLedger();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : (d?.message || formatApiError(e)));
        } finally { setRejectingId(""); }
    };

    // v1.29.90 — Restore a rejected slip back to pending (one-tap Undo).
    const restoreRejected = async (id) => {
        try {
            await api.post(`/uploads/${id}/restore`);
            toast.success("Slip restored to pending");
            reloadLedger();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not restore slip");
        }
    };

    const rejectAllPending = async () => {
        const rows = [...(ledger?.pending_uploads || [])];
        if (!rows.length) return;
        const reason = window.prompt(`Reason for rejecting all ${rows.length} pending slip${rows.length > 1 ? "s" : ""}? (required)`);
        if (reason == null) return;
        if (!reason.trim()) { toast.error("Rejection reason is required"); return; }
        setRejectingAll(true);
        const okIds = []; const fails = [];
        for (const u of rows) {
            try {
                await api.post(`/uploads/${u.id}/verify`, { status: "rejected", notes: reason.trim(), force_upi: true, force_dup: true }, { skipAuthRedirect: true });
                okIds.push(u.id);
            } catch (e) {
                const d = e?.response?.data?.detail;
                fails.push(typeof d === "string" ? d : (d?.message || "failed"));
            }
        }
        if (okIds.length) toast.success(`Rejected ${okIds.length} slip${okIds.length > 1 ? "s" : ""}`, {
            duration: 8000,
            action: {
                label: "Undo all",
                onClick: async () => {
                    for (const id of okIds) { try { await api.post(`/uploads/${id}/restore`); } catch { /* skip */ } }
                    toast.success("Restored to pending");
                    reloadLedger();
                },
            },
        });
        if (fails.length) toast.error(`${fails.length} slip${fails.length > 1 ? "s" : ""} could not be rejected`);
        reloadLedger();
        setRejectingAll(false);
    };

    // v1.28.25 — Un-suspense from the ledger row without leaving the page.
    const unsuspenseFromLedger = async (row) => {
        if (!row?.id) return;
        if (!window.confirm(`Un-suspense this payment (UTR ${row.utr || row.id})?\n\nIt will return to the party's closing balance and any linked commission will be reinstated.`)) return;
        try {
            await api.post(`/transactions/${row.id}/suspense`, { suspense: false, reason: "Cleared from ledger view" });
            toast.success("Payment restored — commission relinked.");
            reloadLedger();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not clear suspense");
        }
    };

    const openEditTxn = (row) => {
        // v1.27.58 — Reset the preset tracker on every dialog open. This
        // guarantees the ORIGINAL saved remark is preserved as-is on load
        // and only gets replaced if the admin actively changes the mode
        // (and the remark is empty or still the last auto-filled preset).
        rectifyLastPresetRef.current = "";
        // v1.27.60 — Refresh presets right before opening the dialog so a
        // preset that was just added / cleared in the Remark Presets page
        // (possibly in another tab) is guaranteed to be reflected here.
        // Without this the auto-fill can silently miss because
        // `paymentModes` was snapshotted at mount time only.
        loadPaymentModes();
        // v1.29.03 — Pre-fill the manual commission / portal-fee override
        // fields from the linked ledger rows (already computed by
        // `_build_ledger_bundle` and folded into the row shape as % and
        // charge amount / fix amount). If a linked row is missing (party
        // has no pricing rule) the fields stay blank so the admin can
        // still enter values from scratch. `_commTouched` / `_feeTouched`
        // start false — a straight Save without editing the panel skips
        // the override payload entirely and hits the auto-recompute path.
        const rowAmt = row._type === "payout" ? row.paid : row.recd;
        const commPct = row.pct ?? row.charge_pct ?? "";
        const commAmt = row.chargeAmt ?? row.charge_amt ?? (commPct !== "" && rowAmt ? Number((rowAmt * Number(commPct) / 100).toFixed(2)) : "");
        const feeAmt = row.fixAmt ?? row.fix_amt ?? "";
        const feePct = feeAmt !== "" && rowAmt ? Number((Number(feeAmt) * 100 / rowAmt).toFixed(4)) : "";
        setEditTxn({
            id: row.id,
            _type: row._type,
            amount: rowAmt,
            date: row.date,
            payment_mode: row.payment_mode || "",
            description: row.notes || "",
            party_id: partyId,          // current party — used for "move to other party" dropdown
            original_party_id: partyId, // to detect a move
            commission_percent: commPct,
            commission_amount: commAmt,
            portal_fee_percent: feePct,
            portal_fee_amount: feeAmt,
            _commTouched: false,
            _feeTouched: false,
        });
    };

    // v1.27.58 — Called from the Payment Mode <Select onValueChange>.
    // Looks up the preset on the newly-picked mode and only fills the
    // Remark field when it's currently empty OR still the last auto-filled
    // preset. Never touches a hand-typed remark.
    const onRectifyModeChange = (newMode) => {
        setEditTxn((prev) => {
            if (!prev) return prev;
            const modeObj = paymentModes.find((m) => (m.name || "").toLowerCase() === (newMode || "").toLowerCase());
            const preset = (modeObj?.default_remark || "").trim();
            const currentRemark = prev.description || "";
            const lastPreset = rectifyLastPresetRef.current || "";
            const canOverwrite = currentRemark === "" || currentRemark === lastPreset;
            // eslint-disable-next-line no-console
            console.debug(`[MICM] Rectify mode change → ${newMode || ""}`, {
                newMode, preset, currentRemark, lastPreset, canOverwrite, modeObj,
            });
            if (canOverwrite && preset) {
                rectifyLastPresetRef.current = preset;
                return { ...prev, payment_mode: newMode, description: preset };
            }
            if (canOverwrite && !preset && currentRemark === lastPreset) {
                rectifyLastPresetRef.current = "";
                return { ...prev, payment_mode: newMode, description: "" };
            }
            return { ...prev, payment_mode: newMode };
        });
    };

    const saveTxn = async (override = false) => {
        if (!editTxn) return;
        // v1.29.42 — crash guard: log start/end, surface failures inline,
        // never let a 401 bounce the app to /login mid-update.
        console.log("[payment-submit] txn update start", { txnId: editTxn.id, override });
        setEditTxnError("");
        try {
            const payload = {
                amount: Number(editTxn.amount),
                date: editTxn.date,
                payment_mode: editTxn.payment_mode || undefined,
                description: editTxn.description || undefined,
            };
            // v1.29.03 — Only send manual commission / portal-fee override
            // fields if the admin actually touched them. `_commTouched` /
            // `_feeTouched` are set by the inputs in the modal; a plain
            // reopen without edits leaves them undefined so the auto
            // pricing-rule recompute continues to fire as before.
            if (editTxn._commTouched) {
                if (editTxn.commission_percent !== "" && editTxn.commission_percent != null) payload.commission_percent = Number(editTxn.commission_percent);
                if (editTxn.commission_amount !== "" && editTxn.commission_amount != null) payload.commission_amount = Number(editTxn.commission_amount);
            }
            if (editTxn._feeTouched) {
                if (editTxn.portal_fee_percent !== "" && editTxn.portal_fee_percent != null) payload.portal_fee_percent = Number(editTxn.portal_fee_percent);
                if (editTxn.portal_fee_amount !== "" && editTxn.portal_fee_amount != null) payload.portal_fee_amount = Number(editTxn.portal_fee_amount);
            }
            const partyChanged = editTxn.party_id && editTxn.party_id !== editTxn.original_party_id;
            if (partyChanged) payload.party_id = editTxn.party_id;
            if (override === true) payload.override_duplicate = true; // v1.29.28 — "Save anyway" on duplicate prompt
            await api.patch(`/transactions/${editTxn.id}`, payload, { skipAuthRedirect: true });
            console.log("[payment-submit] txn update success", { txnId: editTxn.id });
            if (partyChanged) {
                const moved = parties.find((p) => p.id === editTxn.party_id);
                toast.success(`Moved to ${moved?.name || "new party"} — reloading their ledger`);
                setEditTxn(null);
                // Switch the ledger view to the new party so admin sees the moved row
                setPartyId(editTxn.party_id);
            } else {
                toast.success("Transaction updated");
                setEditTxn(null);
                reloadLedger();
            }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            // v1.29.28 — 409 duplicate-entry guard: offer "Save anyway" override.
            if (override !== true && e?.response?.status === 409 && detail?.code === "DUPLICATE_PAYMENT") {
                if (window.confirm(`${detail.message}\n\nSave anyway?`)) return saveTxn(true);
                return;
            }
            console.error("[payment-submit] txn update failed", e?.response?.status, e);
            setEditTxnError(typeof detail === "string" && detail ? detail : formatApiError(e));
            toast.error(typeof detail === "string" && detail ? detail : formatApiError(e));
        }
    };

    // v1.29.43 — Balance Transfer edit/delete. Both sides move together:
    // editing the amount updates Paid on the source party and Recd on the
    // destination party at once; delete removes the entry from both. The
    // 'BALANCE TRANSFER' UTR label is fixed and not editable.
    const openEditTransfer = (row) => {
        setEditTransferError("");
        setEditTransfer({
            refId: row._transferRefId,
            amount: row._transferDir === "out" ? row.paid : row.recd,
            date: row.date,
            remarks: row._transferRemarks || "",
        });
    };

    const saveTransfer = async () => {
        if (!editTransfer) return;
        console.log("[transfer-edit] start", { refId: editTransfer.refId });
        setEditTransferError("");
        const amt = Number(editTransfer.amount);
        if (!amt || amt <= 0) { setEditTransferError("Amount must be greater than zero"); return; }
        if (!editTransfer.date) { setEditTransferError("Date is required"); return; }
        if (!editTransfer.remarks || !editTransfer.remarks.trim()) { setEditTransferError("Remarks are required"); return; }
        try {
            await api.patch(`/transactions/transfer-balance/${editTransfer.refId}`, {
                amount: amt, date: editTransfer.date, remarks: editTransfer.remarks.trim(),
            }, { skipAuthRedirect: true });
            console.log("[transfer-edit] success", { refId: editTransfer.refId });
            toast.success("Balance transfer updated on both parties");
            setEditTransfer(null);
            reloadLedger();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            console.error("[transfer-edit] failed", e?.response?.status, e);
            setEditTransferError(typeof detail === "string" && detail ? detail : formatApiError(e));
            toast.error(typeof detail === "string" && detail ? detail : formatApiError(e));
        }
    };

    const deleteTransfer = async (row) => {
        if (!window.confirm("This will delete the Balance Transfer entry from both parties. Confirm?")) return;
        try {
            await api.delete(`/transactions/transfer-balance/${row._transferRefId}`);
            toast.success("Balance transfer deleted from both parties");
            reloadLedger();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const deleteTxn = async (row) => {
        const label = row._type === "payout" ? "payout" : "payment";
        if (!window.confirm(`Delete this ${label} of ₹${(row._type === "payout" ? row.paid : row.recd).toLocaleString("en-IN")}? This will also remove its linked charges. This cannot be undone.`)) return;
        try {
            await api.delete(`/transactions/${row.id}`);
            toast.success("Transaction deleted");
            reloadLedger();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    // Toggle a row's reconciled flag. Optimistic-update the local ledger so
    // the checkbox flips instantly without a full ledger refetch.
    // v1.20.13 — clear all reconciled flags in the currently-shown date range
    // so admin can re-reconcile that month from scratch. Requires both dates
    // set so only a specific month/period gets reset — historical months stay
    // untouched.
    const resetReconciles = async () => {
        if (!partyId || !dateFrom || !dateTo) {
            toast.error("Set both From and To dates to reset reconcile for that range");
            return;
        }
        if (!window.confirm(`Clear reconcile ticks for ${ledger?.party?.name || "this party"} from ${dateFrom} to ${dateTo}?\n\nOnly this range is affected — other months stay reconciled.`)) return;
        try {
            const { data } = await api.post("/ledger/reconcile-reset", { party_id: partyId, date_from: dateFrom, date_to: dateTo });
            toast.success(`Cleared ${data.cleared} row${data.cleared === 1 ? "" : "s"} — reconcile from scratch`);
            reloadLedger();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        }
    };

    const toggleReconcile = async (row) => {
        const next = !row.reconciled;
        // v1.31.49 — When checking a row in "show unchecked only", keep it
        // visible and fade it out before the filter drops it.
        const willFade = next && reconcileMode && showOnlyUnchecked && (row._type === "payment" || row._type === "payout");
        if (willFade) {
            setFadingRows((s) => new Set(s).add(row.id));
            setTimeout(() => setFadingRows((s) => { const n = new Set(s); n.delete(row.id); return n; }), 850);
        }
        setLedger((prev) => prev ? {
            ...prev,
            rows: prev.rows.map((t) => t.id === row.id ? { ...t, reconciled: next } : t),
        } : prev);
        try {
            await api.post(`/transactions/${row.id}/reconcile`, { reconciled: next });
        } catch (e) {
            // Revert on failure (and cancel any fade so the row stays visible)
            setFadingRows((s) => { const n = new Set(s); n.delete(row.id); return n; });
            setLedger((prev) => prev ? {
                ...prev,
                rows: prev.rows.map((t) => t.id === row.id ? { ...t, reconciled: !next } : t),
            } : prev);
            toast.error(e?.response?.data?.detail || "Reconcile update failed");
        }
    };

    // v1.20.11 — move an unchecked row to Suspense so it stops affecting
    // the party's closing balance. Admin later reassigns it to the correct
    // party from /admin/reports/suspense.
    const moveToSuspense = async (row) => {
        const amt = row.recd || row.paid || 0;
        const reason = window.prompt(
            `Move this ${row._type} of ₹${amt.toLocaleString("en-IN")} to Suspense?\n\nOptional reason:`,
            "",
        );
        if (reason === null) return;
        try {
            await api.post(`/transactions/${row.id}/suspense`, { suspense: true, reason });
            toast.success("Moved to Suspense · view under Reports → Suspense Entries");
            reloadLedger();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not move to suspense");
        }
    };

    // Open a slip's verification drawer, remembering the ledger URL so we can
    // return here automatically when the user closes the drawer.
    // Iter 53 fix: preserve reconcile mode + "show-unchecked-only" filter
    // in the `back=` URL so admin doesn't lose their reconcile progress
    // every time they open a slip to check / edit it.
    const openSlipEdit = (utr, uploadId) => {
        // v1.20.16 — allow opening slips that have no UTR yet (e.g. rejected
        // slips still under review). Fall back to opening by upload_id so the
        // Pencil button in the ledger reconcile view always works.
        if (!utr && !uploadId) return;
        const backParams = new URLSearchParams();
        backParams.set("partyId", partyId);
        if (dateFrom) backParams.set("date_from", dateFrom);
        if (dateTo)   backParams.set("date_to", dateTo);
        if (reconcileMode) backParams.set("reconcile", "1");
        if (showOnlyUnchecked) backParams.set("only_unchecked", "1");
        const back = encodeURIComponent(`/admin/reports?${backParams.toString()}`);
        const stamp = Date.now();
        const key = utr
            ? `utr=${encodeURIComponent(utr)}`
            : `upload_id=${encodeURIComponent(uploadId)}`;
        navigate(`/admin/uploads?${key}&_open=${stamp}&status=all&back=${back}`);
    };

    useEffect(() => {
        loadPaymentModes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        api.get("/parties").then((r) => {
            setParties(r.data);
            // Read initial party from URL (?partyId=…) so we can round-trip from Uploads back to the same ledger
            const url = new URL(window.location.href);
            const pFromUrl = url.searchParams.get("partyId") || url.searchParams.get("party");
            const df = url.searchParams.get("date_from");
            const dt = url.searchParams.get("date_to");
            // Iter 53 — hydrate reconcile view-state from URL so it survives round-trips
            // to the Uploads drawer and back. Without this, admin lost every tick when
            // they clicked into a slip.
            if (url.searchParams.get("reconcile") === "1") setReconcileMode(true);
            if (url.searchParams.get("only_unchecked") === "1") setShowOnlyUnchecked(true);
            if (url.searchParams.get("segment") === "inactive") setPartySegment("inactive");
            if (df) setDateFrom(df);
            if (dt) setDateTo(dt);
            if (pFromUrl) {
                const match = r.data.find((p) => p.id === pFromUrl);
                if (match) {
                    setPartyId(pFromUrl);
                    // v1.29.32 — deep-link without explicit dates → default 7-day window
                    if (!df && !dt) { const w = defaultWeekRange(); setDateFrom(w.from); setDateTo(w.to); }
                    // Auto-switch segment if the URL points at an inactive party
                    if (match.active === false) setPartySegment("inactive");
                    return;
                }
            }
            // v1.24.1 — Do NOT auto-select first party. Admin explicitly asked
            // for the Select-party prompt so they choose intentionally.
        });
    }, []);

    // v1.27.62 — Keep partyId AND every filter in the URL so the browser
    // Back button restores the exact reconcile view. Was: only partyId was
    // synced, so after clicking a Rectify pencil and coming back, dateFrom
    // / dateTo / reconcile / only_unchecked / partySegment all reset to
    // defaults and the admin had to re-pick the party + dates.
    useEffect(() => {
        if (parties.length === 0) return;
        const url = new URL(window.location.href);
        const setOrDel = (k, v) => {
            if (v) url.searchParams.set(k, v);
            else url.searchParams.delete(k);
        };
        setOrDel("partyId", partyId || "");
        setOrDel("date_from", dateFrom || "");
        setOrDel("date_to", dateTo || "");
        setOrDel("reconcile", reconcileMode ? "1" : "");
        setOrDel("only_unchecked", showOnlyUnchecked ? "1" : "");
        setOrDel("segment", partySegment === "inactive" ? "inactive" : "");
        window.history.replaceState({}, "", url.toString());
    }, [partyId, dateFrom, dateTo, reconcileMode, showOnlyUnchecked, partySegment, parties.length]);

    // v1.27.62 — Prev / Next Month navigation on the reconcile toolbar so
    // the admin can flip between months without leaving the page. Snaps
    // both dateFrom and dateTo to the first / last day of the target month
    // (respects Feb / 30-day months naturally via Date arithmetic).
    const shiftMonth = React.useCallback((delta) => {
        if (!partyId) { toast.error("Pick a party first"); return; }
        // Anchor: start from dateFrom if set, else current month.
        const anchor = dateFrom ? new Date(dateFrom) : new Date();
        const y = anchor.getFullYear();
        const m = anchor.getMonth() + delta;
        const first = new Date(y, m, 1);
        const last = new Date(y, m + 1, 0);
        const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        setDateFrom(iso(first));
        setDateTo(iso(last));
    }, [partyId, dateFrom]);

    // v1.27.37 — When toggling the active/inactive segment, clear the current
    // party so the admin consciously picks a new one from the visible segment.
    // Previously the dep also included `parties`, which meant EVERY refetch of
    // the parties list (triggered by any inline edit + reload) would race with
    // this effect and occasionally reset the party mid-edit, kicking the admin
    // back to the empty Account Statement picker. `partySegment` is the ONLY
    // legitimate trigger — parties refresh should never wipe the selection.
    useEffect(() => {
        if (parties.length === 0) return;
        const stillValid = parties.find((p) => p.id === partyId && (partySegment === "inactive" ? p.active === false : p.active !== false));
        if (stillValid) return;
        setPartyId("");
        setLedger(null);
    }, [partySegment]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!partyId) return;
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (showSuspense) params.include_suspense = "true";
        api.get(`/ledger/${partyId}`, { params }).then((r) => setLedger(r.data));
    }, [partyId, dateFrom, dateTo, showSuspense]);

    // v1.29.72 — Restore the per-party saved Payment Mode filter whenever the
    // selected party changes (covers picking a party AND deep-link reopen).
    // The validation effect below reconciles it once the ledger data arrives.
    useEffect(() => {
        if (!partyId) { setModeFilter("ALL"); pendingModeRef.current = null; return; }
        const saved = getSavedMode(partyId);
        setModeFilter(saved);
        pendingModeRef.current = saved;
    }, [partyId]);

    // v1.27.74 — External refresh trigger. When admin uses the Quick-Actions
    // FAB to record a payment / payout, Dashboard finishes the save and
    // fires `micm-ledger-refresh` before navigating back here. Listening
    // for it guarantees the ledger reflects the just-saved row even if
    // v1.28.97 — Auto-open the Party combobox when the user hits Ctrl+L
    // from anywhere in the app. `useGlobalHotkeys` navigates here first
    // then emits a `micm-shortcut` event with action_id "open-ledger"; we
    // also honour a `?focusParty=1` URL flag so a direct-link nav (e.g.
    // from the initial route on first mount) opens the picker too. Both
    // paths race the party list to load — we retry on a short interval
    // until the parties are available so the input never opens empty.
    useEffect(() => {
        const openPicker = () => {
            const tries = [0, 60, 180, 420, 900];
            tries.forEach((ms) => setTimeout(() => partyPickerAutoFocusRef.current?.open?.(), ms));
        };
        const onShortcut = (e) => {
            if (e?.detail?.action_id === "open-ledger") openPicker();
        };
        window.addEventListener("micm-shortcut", onShortcut);
        // Handle direct navigation with ?focusParty=1 on the URL.
        try {
            const u = new URL(window.location.href);
            if (u.searchParams.get("focusParty") === "1") {
                openPicker();
                u.searchParams.delete("focusParty");
                window.history.replaceState({}, "", u.pathname + (u.search || "") + u.hash);
            }
        } catch { /* noop */ }
        return () => window.removeEventListener("micm-shortcut", onShortcut);
    }, []);

    useEffect(() => {
        const onRefresh = () => reloadLedger();
        window.addEventListener("micm-ledger-refresh", onRefresh);
        return () => window.removeEventListener("micm-ledger-refresh", onRefresh);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partyId, dateFrom, dateTo]);

    const filteredRows = ledger?.rows?.filter((r) => {
        if (dateFrom && r.date < dateFrom) return false;
        if (dateTo && r.date > dateTo) return false;
        return true;
    }) || [];

    // v1.25.55 — Recent-updates tracking. Any ledger row whose backend
    // `updated_at` (or fallback `created_at`) is within the last 24 hours
    // gets flagged so admins can spot "what changed since I last looked"
    // at a glance. Used by the top summary card + the amber-left row
    // border + the "Show only recent" filter toggle below the table.
    const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;
    const [showOnlyRecent, setShowOnlyRecent] = React.useState(false);
    const nowMsRef = React.useRef(Date.now());
    // Refresh the "now" reference every 60 s so the recent flag decays
    // naturally without needing a page reload.
    React.useEffect(() => {
        const t = setInterval(() => { nowMsRef.current = Date.now(); }, 60_000);
        return () => clearInterval(t);
    }, []);

    // v1.25.49 — Consolidate: pair each payment with its linked commission +
    // portal_fee for the 1-UTR=1-Row ledger view, MATCHING the PDF/Excel
    // export grouping (`_group_ledger_snapshot_rows` in backend/exports.py).
    // Now indexes commissions/fees by BOTH `upload_id`/`payment_txn_id` AND
    // by `reference` (UTR) so bank-data auto-matches (where the commission
    // is posted with only the UTR, no upload_id link) still fold into the
    // main payment row. Orphaned commission/portal_fee rows (no parent
    // payment on this ledger) are surfaced as standalone rows so nothing
    // silently disappears — again, matching the export.
    const displayRows = React.useMemo(
        () => buildDisplayRows(ledger, dateFrom, dateTo, nowMsRef.current, RECENT_WINDOW_MS),
        [ledger, dateFrom, dateTo]
    );

    // v1.29.60 — Pre-download safety check: re-fetch the ledger (cache-busted)
    // and re-group it with the SAME logic as the screen so the exporter's row
    // count always equals the on-screen "TOTALS · X ROWS". Returns the fresh
    // grouped count (or null on error).
    const verifyLedgerCount = React.useCallback(async () => {
        if (!partyId) return null;
        const p = {
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
            _ts: Date.now(),
        };
        try {
            const { data } = await api.get(`/ledger/${partyId}`, { params: p, headers: { "Cache-Control": "no-cache" } });
            return buildDisplayRows(data, dateFrom, dateTo, Date.now(), RECENT_WINDOW_MS).length;
        } catch {
            return null;
        }
    }, [partyId, dateFrom, dateTo]);

    const formatDate = (iso) => {
        if (!iso) return "—";
        const d = new Date(iso + "T00:00:00");
        if (Number.isNaN(d.getTime())) return iso;
        const p = (n) => String(n).padStart(2, "0");
        return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
    };
    const inr0 = (n) => (n ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n)) : "");
    const inr = (n) => (n ? new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n) : "");

    // v1.25.58 — "Last 10 Updates" dropdown panel (was a dialog). Tracks the
    // MOST RECENT ledger rows on this party's ledger, sorted by updated_at →
    // created_at → date (best-available timestamp), regardless of age.
    // Earlier version wrongly restricted this to the last 24 hours which
    // meant a party like Chirag Jain (whose last few edits happened 3-4 days
    // ago) saw only 2 rows here. Rows updated within 24 h still carry a NEW
    // pill so admins can tell the fresh ones apart from the older ones.
    const [recentPanelOpen, setRecentPanelOpen] = useState(false); // v1.29.31 — dropdown panel toggle (replaces the old modal)
    // Close the panel when the party changes so it never shows stale rows.
    useEffect(() => { setRecentPanelOpen(false); }, [partyId]);
    // v1.29.13 — After the ledger for a NEW party finishes loading, jump
    // the viewport once to whichever end holds the latest entry. Guarded
    // per-partyId so re-fetches (auto-refresh, edits) never yank the user
    // back to the latest row while they're reading somewhere in the middle.
    useEffect(() => {
        if (!ledger || !partyId) return;
        if (didAutoScrollRef.current[partyId]) return;
        didAutoScrollRef.current[partyId] = true;
        const timer = setTimeout(() => {
            const target = (sort?.dir === "asc" ? tableBottomRef : tableTopRef).current;
            target?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
        return () => clearTimeout(timer);
    }, [ledger, partyId]); // eslint-disable-line react-hooks/exhaustive-deps
    const recentAllRows = React.useMemo(() => {
        if (!ledger?.rows) return [];
        const RECENT_MS = 24 * 60 * 60 * 1000;
        const now = Date.now();
        const rows = ledger.rows.map((r) => {
            const ts = r.updated_at || r.created_at || (r.date ? `${r.date}T00:00:00` : null);
            const t = ts ? new Date(ts).getTime() : 0;
            return { ...r, _updatedAt: ts, _sortMs: Number.isFinite(t) ? t : 0, _isFresh: !!(ts && (now - t) <= RECENT_MS) };
        });
        rows.sort((a, b) => b._sortMs - a._sortMs);
        return rows;
    }, [ledger]);
    const recentFreshCount = recentAllRows.filter((r) => r._isFresh).length;

    // v1.29.29 — Three-zone layout: measure the exact height between the
    // bottom of the (variable-height) admin header / preview banners and the
    // enclosing <main>'s bottom padding so the page itself NEVER scrolls,
    // regardless of which banners are showing above the page.
    const pageRootRef = useRef(null);
    const [pageH, setPageH] = useState(null);
    useEffect(() => {
        // Idempotent: same layout inputs always produce the same height, so
        // this can safely re-run on resize, timers, and DOM mutations outside
        // our root (late preview/update banners above the page) without any
        // risk of oscillation or runaway shrink.
        const measure = () => {
            const el = pageRootRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top;
            // v1.29.50 — the root cancels <main>'s vertical padding via negative
            // margins, so fill the full viewport below its live top edge. This
            // makes the bottom summary bar sit flush at the very bottom (no gap
            // below) and removes the white space above the top nav bar.
            // v1.29.97 — On phones, don't pin a fixed height: let the whole page
            // scroll naturally so ALL ledger entries + the summary are reachable
            // (the cramped inner scroll zone only showed 2-3 rows on mobile).
            if (window.innerWidth < 768) { setPageH(null); return; }
            setPageH(Math.max(320, Math.floor(window.innerHeight - top)));
        };
        measure();
        const t1 = setTimeout(measure, 800);
        const t2 = setTimeout(measure, 2500);
        window.addEventListener("resize", measure);
        // Late-appearing banners / prompts change the available height; only
        // react to DOM changes OUTSIDE our root so table re-renders and our
        // own height writes never retrigger measurement.
        const mo = typeof MutationObserver !== "undefined"
            ? new MutationObserver((muts) => {
                const el = pageRootRef.current;
                if (!el) return;
                if (muts.some((m) => !el.contains(m.target))) measure();
            })
            : null;
        if (mo) mo.observe(document.body, { childList: true, subtree: true });
        return () => { window.removeEventListener("resize", measure); clearTimeout(t1); clearTimeout(t2); if (mo) mo.disconnect(); };
    }, []);

    // v1.29.32 — Picking a party resets the date window to the last 7 days
    // (users can widen or narrow it manually afterwards).
    const openParty = (id) => {
        setPartyId(id);
        const w = defaultWeekRange();
        setDateFrom(w.from);
        setDateTo(w.to);
    };

    // v1.29.35 — Pending-slip figures for the in-table pending block and the
    // three-way bottom-bar breakdown. Everything derives from `ledger` /
    // `displayRows` state, so approving a slip (ledger refetch) updates the
    // GRAND totals instantly — no page refresh.
    const pendingSlipRows = ledger?.pending_uploads || [];
    const pendingSlipSum = pendingSlipRows.reduce((s, u) => s + Number(u.amount || 0), 0);
    const matchesMode = (row) => {
        if (modeFilter === "ALL") return true;
        if (modeFilter === "CREDIT") return Number(row.recd || 0) > 0;
        if (modeFilter === "DEBIT") return Number(row.paid || 0) > 0;
        return (row.payment_mode || "") === modeFilter;
    };
    // v1.29.72 — Modes present in the CURRENT (date-filtered) ledger view.
    const availableModes = Array.from(new Set((ledger?.rows || []).map((r) => (r.payment_mode || "").trim()).filter(Boolean))).sort();
    // A saved mode is "known" if it's visible in the current window OR a
    // configured company payment mode. This stops the date filter (default
    // 7-day reopen window) from wrongly flagging a valid saved mode as gone —
    // ledger.rows itself is server-side date-filtered, so it can't be trusted
    // alone to decide whether a party ever used a given mode.
    const knownModeSet = React.useMemo(() => {
        const s = new Set(availableModes);
        (paymentModes || []).forEach((m) => { const n = (m?.name || "").trim(); if (n) s.add(n); });
        return s;
    }, [availableModes, paymentModes]);
    // Dropdown always lists the current-window modes PLUS the active selection
    // so a restored mode with no rows in this window still shows as selected.
    const modeOptions = Array.from(new Set([
        ...availableModes,
        ...(modeFilter && !["ALL", "CREDIT", "DEBIT"].includes(modeFilter) ? [modeFilter] : []),
    ])).sort();
    // v1.29.72 — Validate the restored mode once BOTH the ledger and the
    // company payment-mode list have loaded (avoids a race that would wipe a
    // valid saved mode on first paint). A mode that is neither visible in this
    // window nor a known company mode is treated as removed → ALL + toast.
    useEffect(() => {
        const pending = pendingModeRef.current;
        if (!pending || !ledger) return;
        if (pending === "ALL" || pending === "CREDIT" || pending === "DEBIT") { pendingModeRef.current = null; return; }
        if (!paymentModes || paymentModes.length === 0) return; // wait for company modes
        pendingModeRef.current = null;
        if (!knownModeSet.has(pending)) {
            setModeFilter("ALL");
            saveMode(partyId, "ALL");
            toast.info(`"${pending}" payments not found for this party — showing all`);
        }
    }, [ledger, partyId, paymentModes, knownModeSet]); // eslint-disable-line react-hooks/exhaustive-deps
    // v1.30.1 — Unified search haystack across every visible column so the
    // quick search matches Date, UTR, Received/%/Comm/Net/Fix/Paid/Balance and
    // the Remark text — including balance-transfer remarks (_transferRemarks)
    // and the suspense reason, which the old haystack silently dropped. Case-
    // insensitive substring match; kept in ONE place so all four call sites
    // (row filter, header counter, table body, search subtotal) stay in sync.
    const rowHaystack = (row) => [
        row.date, row.utr, row.remark, row.notes, row._transferRemarks,
        row.payment_mode, row.suspense_reason,
        row.recd, row.pctAmt, row.netAmt, row.fixAmt, row.paid, row.balance,
    ].map((v) => (v === null || v === undefined ? "" : String(v))).join(" ").toLowerCase();
    const passesRowFilters = (row) => {
        if (showOnlyRecent && !row._isRecent) return false;
        if (suspenseOnly && !row.is_suspense) return false;
        if (shouldHideUncheckedRow(row)) return false;
        if (!matchesMode(row)) return false;
        const q = (rowSearch || "").trim().toLowerCase();
        if (q && !rowHaystack(row).includes(q)) return false;
        return true;
    };
    const viewRows = displayRows.filter(passesRowFilters);
    const verifiedReceived = viewRows.reduce((s, r) => s + Number(r.recd || 0), 0);
    const verifiedPaid = viewRows.reduce((s, r) => s + Number(r.paid || 0), 0);
    const verifiedClosing = Number(ledger?.closing_balance || 0);

    // v1.29.36 — Bulk Move Entry: PATCH each selected row to the chosen party,
    // serially (mirrors MoveEntryDialog's bulk mode so a 409 duplicate-guard
    // failure on one row doesn't leave the rest ambiguous), then deselect all
    // and refresh the ledger. Single-row move via the row icon is unchanged.
    const bulkMoveSelected = async () => {
        if (!bulkMoveTarget) { toast.error("Pick a destination party first"); return; }
        const rows = displayRows.filter((r) => selectedIds.has(r.id));
        if (!rows.length) return;
        setBulkMoveBusy(true);
        let ok = 0;
        const failures = [];
        for (const r of rows) {
            try {
                await api.patch(`/transactions/${r.id}`, { party_id: bulkMoveTarget });
                ok += 1;
            } catch (e) {
                failures.push({ id: r.id, err: formatApiError(e) });
            }
        }
        const partyName = (parties.find((p) => p.id === bulkMoveTarget) || {}).name || "party";
        if (ok > 0 && failures.length === 0) toast.success(`${ok} ${ok === 1 ? "entry" : "entries"} moved to ${partyName}`);
        else if (ok > 0) {
            toast.warning(`Moved ${ok} of ${rows.length}. ${failures.length} failed — see console.`);
            // eslint-disable-next-line no-console
            console.warn("[Bulk Move] partial failure:", failures);
        } else toast.error(failures[0]?.err || "Move failed");
        if (ok > 0) { setSelectedIds(new Set()); setBulkMoveTarget(""); reloadLedger(); }
        setBulkMoveBusy(false);
    };

    return (
        <div
            ref={pageRootRef}
            className="-mx-4 -mt-6 -mb-24 flex flex-col overflow-hidden sm:-mx-6 sm:-mt-8 sm:-mb-8"
            style={pageH ? { height: `${pageH}px` } : undefined}
            data-testid="reports-page"
        >
            {/* ===== v1.29.29 — TOP STICKY BAR. Two compact rows on a dark bar
                (matches the bottom summary bar); it never scrolls. ===== */}
            <div className="shrink-0 space-y-1.5 border-b-2 border-slate-950 bg-slate-900 px-3 py-1.5 text-white shadow-md sm:space-y-2 sm:px-5 sm:py-2.5" data-testid="ledger-top-bar">
            {/* Row 1 — party picker + From/To dates + Clear/Print (left) · action buttons (right) */}
            {/* v1.29.32 — Row 1 flattened to ONE line: party picker → dates →
                Clear/Print → action buttons. The "Party Ledger" title block was
                removed to guarantee a single row at ~1366px (assigned keyboard
                shortcuts keep working — the global hotkey listener is separate). */}
            <div className="flex flex-wrap items-center gap-2">
                    <div className="w-44">
                        <PartyCombobox
                            parties={visibleParties}
                            value={partyId}
                            onChange={openParty}
                            placeholder={visibleParties.length ? "Select party" : (partySegment === "inactive" ? "No inactive parties" : "No parties")}
                            autoFocusRef={partyPickerAutoFocusRef}
                        />
                    </div>
                    {/* v1.29.33 — label+input paired with explicit gap so the
                        "To" label can never sit flush/overlap the FROM input;
                        date inputs widened to w-40 (native picker needs it). */}
                    <span className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">From</span>
                        <Input data-testid="report-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40 border-slate-500 bg-transparent text-sm text-white placeholder:text-slate-400 [color-scheme:dark]" />
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">To</span>
                        <Input data-testid="report-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40 border-slate-500 bg-transparent text-sm text-white placeholder:text-slate-400 [color-scheme:dark]" />
                    </span>
                    <button
                        onClick={() => { setDateFrom(""); setDateTo(""); }}
                        data-testid="report-clear-dates"
                        className="h-9 rounded-md border border-slate-500 px-3 text-xs font-semibold text-slate-200 transition hover:bg-slate-800"
                    >
                        Clear
                    </button>
                    <button
                        data-testid="print-btn"
                        onClick={() => window.print()}
                        className="h-9 rounded-md bg-white px-3 text-xs font-semibold text-slate-900 transition hover:bg-slate-200"
                    >
                        Print
                    </button>
                    {partyId && (
                        <button
                            type="button"
                            data-testid="last-10-updates-btn"
                            onClick={() => setRecentPanelOpen((v) => !v)}
                            aria-expanded={recentPanelOpen}
                            title={`${recentPanelOpen ? "Hide" : "Show"} the recent-updates panel${recentFreshCount > 0 ? ` · ${recentFreshCount} updated in the last 24 h` : ""}`}
                            className={`relative inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold shadow-sm transition ${recentPanelOpen ? "border-amber-400 bg-amber-500 text-white hover:bg-amber-600" : "border-amber-300 bg-white text-amber-800 hover:bg-amber-50"}`}
                        >
                            <History size={15} aria-hidden />
                            <span>Last 10 Updates</span>
                            {recentFreshCount > 0 && (
                                <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" title={`${recentFreshCount} updated in the last 24 h`}>
                                    {recentFreshCount > 99 ? "99+" : recentFreshCount} NEW
                                </span>
                            )}
                        </button>
                    )}
                    {partyId && (
                        <ShareLedgerButton
                            party={ledger?.party}
                            closingBalance={ledger?.closing_balance}
                        />
                    )}
                    {partyId && (
                        <ExportButton
                            endpoint={{ pdf: `/export/ledger/${partyId}.pdf`, xlsx: `/export/ledger/${partyId}.xlsx` }}
                            params={{
                                ...(dateFrom ? { date_from: dateFrom } : {}),
                                ...(dateTo ? { date_to: dateTo } : {}),
                                ...(reconcileMode ? { markers: 1 } : {}),
                            }}
                            filenameBase={`ledger-${(ledger?.party?.name || "party").replace(/\s+/g, "_")}`}
                            testId="report-ledger-export"
                            expectedCount={displayRows.length}
                            verifyCount={verifyLedgerCount}
                            label="Download Ledger"
                            variant="primary"
                            iconOnly
                            tooltip="Download Ledger"
                            icon={<Download size={16} />}
                            pdfIcon={<FileText size={15} className="mr-2 text-red-600" />}
                            xlsxIcon={<FileSpreadsheet size={15} className="mr-2 text-emerald-600" />}
                        />
                    )}
                    {partyId && (
                        <AuditTrailDialog partyId={partyId} partyName={ledger?.party?.name} />
                    )}
                    {/* v1.29.45 — Compact inline slips badge (replaces the old
                        "Outstanding Report" button, which now lives in the
                        Reports tab). Shows range slip counts; click for detail. */}
                    {partyId && ledger?.range_stats && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="ledger-slips-badge"
                                    title="Slips uploaded in the selected date range"
                                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-500 px-3 text-[11px] font-semibold text-slate-200 transition hover:bg-slate-800"
                                >
                                    <span className="uppercase tracking-wider text-slate-400">Slips:</span>
                                    <span data-testid="slips-badge-total">{ledger.range_stats.total_count} Total</span>
                                    <span className="text-slate-500">·</span>
                                    <span className="text-emerald-300" data-testid="slips-badge-verified">{ledger.range_stats.verified_count} Verified</span>
                                    <span className="text-slate-500">·</span>
                                    <span className="text-amber-300" data-testid="slips-badge-pending">{ledger.range_stats.pending_count} Pending</span>
                                    <span className="text-slate-500">·</span>
                                    <span className="text-rose-300" data-testid="slips-badge-rejected">{ledger.range_stats.rejected_count} Rejected</span>
                                    <ChevronDown size={12} className="opacity-70" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-72" data-testid="slips-badge-popover">
                                <div className="text-xs uppercase tracking-[0.2em] text-blue-700/70">
                                    Slips uploaded · {ledger.range_stats.date_from || "…"} → {ledger.range_stats.date_to || "…"}
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-3">
                                    <MiniStat label="Total" value={ledger.range_stats.total_count} testId="rs-total" />
                                    <MiniStat label="Verified" value={ledger.range_stats.verified_count} tone="emerald" testId="rs-verified" />
                                    <MiniStat label="Pending" value={ledger.range_stats.pending_count} tone="amber" testId="rs-pending" />
                                    <MiniStat label="Rejected" value={ledger.range_stats.rejected_count} tone="rose" testId="rs-rejected" />
                                </div>
                                <div className="mt-3 text-sm text-slate-600">
                                    Verified amount: <span className="font-mono font-semibold text-emerald-700">{fmt(ledger.range_stats.verified_amount)}</span>
                                </div>
                                {(ledger.range_stats.rejected_uploads || []).length > 0 && (
                                    <div className="mt-3 border-t border-slate-100 pt-3" data-testid="rejected-slips-list">
                                        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-rose-600">
                                            Rejected slips · why
                                        </div>
                                        <div className="max-h-48 space-y-2 overflow-auto">
                                            {ledger.range_stats.rejected_uploads.map((rj) => (
                                                <div key={rj.id} data-testid={`rejected-slip-${rj.id}`} className="rounded-md border border-rose-100 bg-rose-50/60 px-2.5 py-2">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="truncate font-mono text-xs text-slate-600">UTR {rj.utr || "—"}</span>
                                                        <span className="shrink-0 font-semibold tabular-nums text-slate-800">{fmt(rj.amount)}</span>
                                                    </div>
                                                    <div className="mt-0.5 text-[12px] leading-snug text-rose-800" data-testid={`rejected-reason-${rj.id}`}>
                                                        {rj.reason ? rj.reason : <span className="italic text-slate-400">No reason recorded</span>}
                                                    </div>
                                                    <div className="mt-1 flex items-center justify-between gap-2">
                                                        <span className="text-[10px] text-slate-400">{dmY(rj.payment_date)}{rj.rejected_by ? ` · by ${rj.rejected_by}` : ""}</span>
                                                        <button
                                                            type="button"
                                                            data-testid={`rejected-restore-${rj.id}`}
                                                            onClick={() => restoreRejected(rj.id)}
                                                            className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 transition hover:bg-blue-50"
                                                        >
                                                            Restore
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </PopoverContent>
                        </Popover>
                    )}
            </div>

            {/* Row 2 — Active/Inactive pill tabs · ledger tools · UTR/Remark
                search. v1.29.29 — these are the SAME controls that used to
                live in the party-segment strip, the filter card and the
                in-card reconcile toolbar; only their location changed.
                All handlers + data-testids are unchanged. */}
            {/* v1.29.50 — Active/Inactive tabs removed and the whole tools row is
                gated on `ledger`, so no empty second row remains when no party is
                selected. Inactive Parties moved to the Reports tab
                (/admin/reports?segment=inactive) — underlying logic unchanged. */}
            {ledger && (
            <div className="flex flex-nowrap items-center gap-1 overflow-hidden whitespace-nowrap" data-testid="ledger-tools-row">
                {ledger && (
                <div className="contents" data-testid="reconcile-toolbar">
                    {/* v1.29.51 — "Oldest/Newest First" quick toggle removed; sort
                        is controlled by clicking the Date column header (SortHeader). */}
                    <button
                        type="button"
                        data-testid="reconcile-mode-toggle"
                        onClick={() => { setReconcileMode((v) => !v); setShowOnlyUnchecked(false); }}
                        title="Tap each row's checkbox to mark it as reviewed. Unchecked rows are your discrepancies."
                        className={`inline-flex items-center gap-1.5 rounded-full px-[6px] py-[3px] text-[11px] font-semibold uppercase tracking-wider transition-colors ${reconcileMode ? "bg-blue-600 text-white shadow-sm" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"}`}
                    >
                        {reconcileMode ? "✓ Reconcile ON" : "Reconcile Mode"}
                    </button>
                    <button
                        type="button"
                        data-testid="move-transactions-toggle"
                        onClick={() => setMoveMode((v) => { const nv = !v; if (!nv) { setSelectedIds(new Set()); setBulkMoveTarget(""); } return nv; })}
                        title="Show tick-boxes to select entries and move them to another party"
                        className={`inline-flex items-center gap-1.5 rounded-full px-[6px] py-[3px] text-[11px] font-semibold uppercase tracking-wider transition-colors ${moveMode ? "bg-blue-600 text-white shadow-sm" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"}`}
                    >
                        {moveMode ? "✓ Move Transactions ON" : "Move Transactions"}
                    </button>
                    {/* v1.28.25 — Show Suspensed Entries toggle. OFF by default
                        (v1.31.36) — a suspensed entry is parked, so it is hidden
                        from the party ledger; turn this ON to review parked rows
                        with the orange SUSPENSE badge + reason + Un-suspense action. */}
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-orange-300 bg-white px-[6px] py-[3px] text-[11px] font-semibold text-orange-800 hover:bg-orange-50" data-testid="show-suspense-toggle-label">
                        <input
                            type="checkbox"
                            data-testid="show-suspense-toggle"
                            checked={showSuspense}
                            onChange={(e) => { setShowSuspense(e.target.checked); if (!e.target.checked) { setSuspenseOnly(false); setSelectedSuspense(new Set()); } }}
                            className="h-3.5 w-3.5 cursor-pointer accent-orange-600"
                        />
                        <span className="uppercase tracking-wider">Show Suspensed</span>
                    </label>
                    {showSuspense && (() => {
                        const suspRows = displayRows.filter((r) => r.is_suspense);
                        const suspCount = suspRows.length;
                        return (
                            <>
                                <button
                                    type="button"
                                    data-testid="suspense-only-chip"
                                    onClick={() => setSuspenseOnly((v) => !v)}
                                    disabled={suspCount === 0}
                                    className={`inline-flex items-center gap-1 rounded-full px-[6px] py-[3px] text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${suspenseOnly ? "bg-orange-500 text-white shadow-sm" : "border border-orange-300 bg-white text-orange-800 hover:bg-orange-50"}`}
                                    title={suspCount === 0 ? "No suspense rows in this range" : "Show ONLY suspense rows"}
                                >
                                    Suspense-only <span className="rounded-full bg-white/30 px-1 text-[10px]">{suspCount}</span>
                                </button>
                                {selectedSuspense.size > 0 && (
                                    <button
                                        type="button"
                                        data-testid="bulk-unsuspense-btn"
                                        onClick={bulkUnsuspense}
                                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-[6px] py-[3px] text-[11px] font-bold uppercase tracking-wider text-white shadow-sm hover:bg-emerald-700"
                                    >
                                        ↺ Un-suspense selected · {selectedSuspense.size}
                                    </button>
                                )}
                            </>
                        );
                    })()}
                    {/* v1.27.66 — Prev / Next Month always visible when a party is loaded,
                        regardless of Reconcile Mode. */}
                    <button
                        type="button"
                        data-testid="ledger-prev-month"
                        onClick={() => shiftMonth(-1)}
                        disabled={!partyId}
                        title="View the previous month"
                        className="rounded-md border border-slate-300 bg-white px-[6px] py-[3px] text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        ◀ Prev Month
                    </button>
                    <button
                        type="button"
                        data-testid="ledger-next-month"
                        onClick={() => shiftMonth(+1)}
                        disabled={!partyId}
                        title="View the next month"
                        className="rounded-md border border-slate-300 bg-white px-[6px] py-[3px] text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Next Month ▶
                    </button>
                    {reconcileMode && (() => {
                        const reconcilable = displayRows.filter((r) => r._type === "payment" || r._type === "payout");
                        const done = reconcilable.filter((r) => r.reconciled).length;
                        const remaining = reconcilable.length - done;
                        const pct = reconcilable.length ? Math.round((done / reconcilable.length) * 100) : 0;
                        return (
                            <>
                                <div className="flex items-center gap-1" data-testid="reconcile-progress">
                                    <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200">
                                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="font-mono text-[10px] text-slate-200">
                                        <span className="font-bold text-emerald-300" data-testid="reconcile-done">{done}</span>
                                        /{reconcilable.length}
                                        {remaining > 0 && <span className="ml-1 font-semibold text-rose-300" data-testid="reconcile-remaining">· {remaining} left</span>}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    data-testid="reconcile-only-unchecked"
                                    onClick={() => setShowOnlyUnchecked((v) => !v)}
                                    className={`rounded-md px-[6px] py-[3px] text-[11px] font-medium ${showOnlyUnchecked ? "bg-rose-600 text-white" : "border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"}`}
                                >
                                    {showOnlyUnchecked ? "Showing unchecked only" : "Show unchecked only"}
                                </button>
                                <button
                                    type="button"
                                    data-testid="reconcile-reset-range"
                                    onClick={resetReconciles}
                                    disabled={!dateFrom || !dateTo}
                                    title={!dateFrom || !dateTo ? "Set From and To dates first" : `Clear reconcile ticks for ${dateFrom} → ${dateTo} so you can start over`}
                                    className="rounded-md border border-blue-200 bg-blue-50 px-[6px] py-[3px] text-[11px] font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    ↻ Reset reconcile in range
                                </button>
                            </>
                        );
                    })()}
                    {/* v1.29.50 — reconcile instruction text is now a hover tooltip
                        on the Reconcile Mode button (removed from the row). */}
                </div>
                )}
                {/* v1.29.19 — UTR / Remark quick search (moved into the top bar in
                    v1.29.29). Same case-insensitive UTR/Remark/notes substring match,
                    same "Showing X of Y" counter; state stays local to the page. */}
                {ledger && displayRows.length > 0 && (() => {
                    const q = (rowSearch || "").trim().toLowerCase();
                    const preSearchRows = sortRows(displayRows, sort).filter((row) => {
                        if (showOnlyRecent && !row._isRecent) return false;
                        if (suspenseOnly && !row.is_suspense) return false;
                        if (shouldHideUncheckedRow(row)) return false;
                        if (!matchesMode(row)) return false;
                        return true;
                    });
                    const matches = q
                        ? preSearchRows.filter((r) => rowHaystack(r).includes(q))
                        : preSearchRows;
                    return (
                        <div className="flex items-center" data-testid="ledger-row-search-bar">
                            <div className="relative w-[130px] min-w-[130px] max-w-[130px]">
                                <MagnifyingGlass size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="search"
                                    data-testid="ledger-row-search-input"
                                    value={rowSearch}
                                    onChange={(e) => setRowSearch(e.target.value)}
                                    placeholder="Search…"
                                    className="w-full rounded-md border border-slate-300 bg-white py-[3px] pl-6 pr-11 text-[11px] text-slate-800 placeholder-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                />
                                <span
                                    data-testid="ledger-row-search-count"
                                    className={`pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 rounded px-1 py-0.5 text-[10px] font-bold tabular-nums ${q ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}
                                >
                                    {matches.length} of {preSearchRows.length}
                                </span>
                            </div>
                        </div>
                    );
                })()}
                {ledger && (displayRows.length > 0 || modeFilter !== "ALL") && (
                    <div className="flex items-center" data-testid="ledger-mode-filter-wrap">
                        <select
                            data-testid="ledger-mode-filter"
                            value={modeFilter}
                            onChange={(e) => { setModeFilter(e.target.value); saveMode(partyId, e.target.value); }}
                            title="Filter by Mode"
                            className="ml-2 rounded-md border border-slate-300 bg-white py-[3px] pl-2 pr-6 text-[11px] font-semibold text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        >
                            <option value="ALL">Filter by Mode: ALL</option>
                            <option value="CREDIT">CREDIT ONLY</option>
                            <option value="DEBIT">DEBIT ONLY</option>
                            {modeOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                )}
                {/* yellow Last-24-hours panel (right of the search bar). Same
                    showOnlyRecent state + filter behaviour, unchanged. */}
                {ledger && (() => {
                    const recentCount = displayRows.filter((r) => r._isRecent).length;
                    return (
                        <button
                            type="button"
                            data-testid="ledger-recent-toggle"
                            onClick={() => setShowOnlyRecent((v) => !v)}
                            disabled={recentCount === 0}
                            className={`max-w-[110px] overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-[6px] py-[3px] text-[11px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-40 ${
                                showOnlyRecent
                                    ? "bg-amber-600 text-white shadow-sm hover:bg-amber-700"
                                    : "border border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                            }`}
                        >
                            {showOnlyRecent ? "Showing recent only · tap to reset" : "Show recent only"}
                        </button>
                    );
                })()}
            </div>
            )}
            </div>

            {/* v1.29.31 — "Last 24 hours" recent-updates panel. Hidden on page
                load (occupies NO space); the "Last 10 Updates" button in the
                top bar toggles it as a dropdown directly below the bar. When
                hidden, the table's flex-1 scroll zone fills the space — no
                empty gap. Rows/timestamps/badges/amounts as before. */}
            {recentPanelOpen && ledger && (() => {
                const recentRows = displayRows.filter((r) => r._isRecent);
                const recentCount = recentRows.length;
                const last10 = [...recentRows]
                    .sort((a, b) => new Date(b._updatedAt || 0) - new Date(a._updatedAt || 0))
                    .slice(0, 10);
                const recentAmt = recentRows.reduce((s, r) => s + Number(r.recd || 0) - Number(r.paid || 0), 0);
                return (
                    <div data-testid="ledger-recent-card" className="shrink-0 border-b border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 shadow-md">
                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white shadow-inner text-[13px]" aria-hidden>🕐</div>
                                <div className="leading-tight">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-base font-bold uppercase tracking-[0.15em] text-amber-900">Last 24 hours</span>
                                        <span data-testid="ledger-recent-count" className="rounded-full bg-white px-2 py-0.5 text-sm font-bold text-amber-800 ring-1 ring-amber-300">
                                            {recentCount} {recentCount === 1 ? "update" : "updates"}
                                        </span>
                                        {last10[0]?._updatedAt && (
                                            <span className="text-xs italic text-slate-500">
                                                · latest {new Date(last10[0]._updatedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-base text-slate-600">
                                        Net movement:&nbsp;
                                        <span className={`font-semibold ${recentAmt >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                                            {recentAmt >= 0 ? "+" : ""}{inr(recentAmt)}
                                        </span>
                                        {last10.length > 0 && (
                                            <span className="ml-2 text-slate-400">· showing last {last10.length} of {recentCount}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {last10.length > 0 && (
                            <ul
                                data-testid="ledger-recent-list"
                                className="border-t border-amber-200 bg-white/80 divide-y divide-amber-100"
                            >
                                {last10.map((r, idx) => {
                                    const amt = Number(r.recd || 0) || -Number(r.paid || 0);
                                    const ts = r._updatedAt ? new Date(r._updatedAt) : null;
                                    return (
                                        <li
                                            key={r.id}
                                            data-testid={`ledger-recent-item-${idx}`}
                                            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-1.5 text-xs"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800">
                                                    #{idx + 1}
                                                </span>
                                                <span className="text-slate-500 whitespace-nowrap">{ts ? (() => { const p = (n) => String(n).padStart(2, "0"); return `${p(ts.getDate())}/${p(ts.getMonth() + 1)} ${p(ts.getHours())}:${p(ts.getMinutes())}`; })() : (r.date || "")}</span>
                                                <span className="font-mono text-slate-700 truncate max-w-[160px]" title={r.utr}>{r.utr || "—"}</span>
                                                <span className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                                                    r.remark === "APPROVED" ? "bg-emerald-100 text-emerald-800" :
                                                    r.remark === "PAID" || r._type === "payout" ? "bg-violet-100 text-violet-800" :
                                                    r.remark === "COMMISSION" ? "bg-slate-100 text-slate-700" :
                                                    r.remark === "PORTAL FEE" ? "bg-amber-100 text-amber-800" :
                                                    "bg-slate-100 text-slate-700"
                                                }`}>
                                                    {r.remark}
                                                </span>
                                            </div>
                                            <span className={`font-mono tabular-nums font-semibold ${amt >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                                                {amt >= 0 ? "+" : ""}{inr0(amt)}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {recentCount === 0 && (
                            <div className="border-t border-amber-200 px-4 py-2 text-xs italic text-amber-800" data-testid="ledger-recent-empty">
                                No updates in the last 24 hours on this ledger.
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* ===== v1.29.29 — MIDDLE scroll zone. Only this zone scrolls;
                the top bar above and the summary bar below never move. ===== */}
            <div className="overflow-visible px-1 pb-3 md:flex-1 md:min-h-0 md:overflow-auto md:overscroll-contain sm:px-2" data-testid="ledger-scroll-zone">

            {!ledger ? (
                <div className="pt-6"><EmptyState title="Select a party" description="Choose a party above to view the running-balance ledger." /></div>
            ) : (
                <div className="space-y-4 pt-3">
                    {/* v1.29.29 — PARTY stat card, SUMMARY CARDS ROW and the two
                        push-notification banner cards removed (figures live in the
                        bottom bar; push status is now the bell on the ledger card).
                        range_stats + pending-uploads cards stay unchanged. */}
                    {/* v1.29.45 — "SLIPS UPLOADED" range_stats card removed from
                        here; its figures now live in the compact inline slips
                        badge on the top nav bar. */}

                    {/* v1.29.47 — pending-slips banner removed from here; it is
                        now a floating amber icon (bottom-right, above the sticky
                        bar) with a slide-in review panel. See PendingSlipsFab
                        block near the end of this component. */}

                    <div className="relative rounded-lg border border-slate-200 bg-white shadow-sm">
                        {/* v1.29.29 — single bell replaces the old push banner cards.
                            Red pulse = not enabled · amber pulse = no pushes in 7d ·
                            green = healthy. Hover for status, click = test push. */}
                        <LedgerNotifyBell partyId={partyId} partyName={ledger.party?.name} />
                        {/* v1.29.31 — the "Last 24 hours" panel moved OUT of the
                            card: it is now a dropdown below the top sticky bar,
                            toggled by the "Last 10 Updates" button (hidden on
                            page load). The "Show recent only" toggle moved to
                            top bar row 2, right of the search bar. */}
                        {/* v1.29.30 — In-card reconcile toolbar + UTR/Remark search
                            removed: both live in the top sticky bar (row 2) since
                            v1.29.29. The duplicates rendered here were leftovers. */}
                        {/* v1.29.36 — Bulk Move Entry toolbar. Appears directly above
                            the table whenever one or more rows are checkbox-selected.
                            Inline party dropdown + Confirm Move; the per-row single
                            move icon (MoveEntryDialog) is unchanged. */}
                        {moveMode && selectedIds.size > 0 && (
                            <div data-testid="ledger-bulk-move-bar" className="flex flex-wrap items-center gap-2 border-b border-blue-200 bg-blue-50 px-3 py-2">
                                <ArrowsLeftRight size={14} weight="bold" className="text-blue-700" />
                                <span data-testid="ledger-bulk-move-label" className="text-xs font-bold uppercase tracking-wider text-blue-900">
                                    Move {selectedIds.size} {selectedIds.size === 1 ? "entry" : "entries"} to:
                                </span>
                                <select
                                    data-testid="bulk-move-target"
                                    value={bulkMoveTarget}
                                    onChange={(e) => setBulkMoveTarget(e.target.value)}
                                    className="h-8 rounded-md border border-blue-300 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="">Select party…</option>
                                    {parties.filter((p) => p.active !== false && p.id !== partyId).map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    data-testid="bulk-move-confirm"
                                    onClick={bulkMoveSelected}
                                    disabled={!bulkMoveTarget || bulkMoveBusy}
                                    className="h-8 rounded-md bg-blue-600 px-3 text-xs font-bold uppercase tracking-wider text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    {bulkMoveBusy ? "Moving…" : "Confirm Move"}
                                </button>
                                <button
                                    type="button"
                                    data-testid="bulk-move-clear"
                                    onClick={() => { setSelectedIds(new Set()); setBulkMoveTarget(""); }}
                                    className="ml-auto text-xs font-semibold text-slate-500 underline underline-offset-2 hover:text-slate-700"
                                >
                                    Clear selection
                                </button>
                            </div>
                        )}
                        <div ref={tableTopRef} data-testid="ledger-top-marker" aria-hidden />
                        {/* v1.29.97 — Mobile card list: no horizontal scroll, and the
                            page (not a tiny inner zone) scrolls so many entries show. */}
                        <div className="space-y-2 pt-2 md:hidden" data-testid="ledger-cards">
                            {displayRows.length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-400">No transactions in range</div>
                            ) : displayRows.map((row) => (
                                <div
                                    key={`c-${row.id}`}
                                    data-testid={`ledger-card-${row.id}`}
                                    onClick={() => { if (row.upload_id) openSlipEdit(row.utr, row.upload_id); }}
                                    className={`rounded-lg border p-3 ${row.is_suspense ? "border-orange-400 bg-orange-100" : row.ocr_mismatch ? "border-rose-400 bg-rose-50" : row.reconciled ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-xs text-slate-500">{dmY(row.date)}</div>
                                            <div className="mt-0.5 break-all font-mono text-sm font-semibold text-slate-800">
                                                {row._type === "payout" ? (row.payment_mode || "CASH PAID") : (row.utr || "CASH")}
                                            </div>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <div className={`font-bold tabular-nums ${balanceCls(row.balance)}`}>{inr(row.balance)}</div>
                                            <div className="mt-0.5 space-x-2 text-xs tabular-nums">
                                                {row.recd ? <span className="text-emerald-700">+{inr0(row.recd)}</span> : null}
                                                {row.paid ? <span className="text-violet-700">−{inr0(row.paid)}</span> : null}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                                        {row.netAmt ? <span>Net {inr0(row.netAmt)}</span> : null}
                                        {row.pct ? <span>{row.pct.toFixed(2)}%</span> : null}
                                        {row.is_suspense && <span className="font-semibold text-orange-600">SUSPENSE</span>}
                                        {row.upload_id && <span className="ml-auto inline-flex items-center gap-1 text-blue-600"><Clock size={12} /> slip</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="hidden md:block">
                            <table className="w-full text-base sm:text-lg" data-testid="ledger-table">
                                <thead className="sticky top-0 z-10">{/* v1.29.29 — headers stay pinned while the middle zone scrolls */}
                                    <tr className="bg-emerald-700 text-left text-xs font-semibold uppercase tracking-wide text-white">
                                        {/* v1.29.23 — Select-All checkbox. Only ticks currently-visible
                                            rows (post-filter) so hidden entries can never leak into a
                                            bulk action. */}
                                        {moveMode && (
                                        <th className="px-2 py-2 text-center border-r border-emerald-600 w-10">
                                            <input
                                                type="checkbox"
                                                data-testid="ledger-select-all"
                                                aria-label="Select all visible rows"
                                                checked={(() => {
                                                    const ids = window.__reportsVisibleIds || [];
                                                    return ids.length > 0 && ids.every((id) => selectedIds.has(id));
                                                })()}
                                                onChange={() => {
                                                    const ids = window.__reportsVisibleIds || [];
                                                    setSelectedIds((prev) => {
                                                        const next = new Set(prev);
                                                        const allChecked = ids.length > 0 && ids.every((id) => next.has(id));
                                                        if (allChecked) ids.forEach((id) => next.delete(id));
                                                        else ids.forEach((id) => next.add(id));
                                                        return next;
                                                    });
                                                }}
                                                className="h-4 w-4 cursor-pointer"
                                            />
                                        </th>
                                        )}
                                        {reconcileMode && <th className="px-2 py-2 text-center border-r border-emerald-600 w-10">✓</th>}
                                        <th className="px-2 py-2 border-r border-emerald-600"><SortHeader field="date" sort={sort} onChange={setSort}>Date</SortHeader></th>
                                        <th className="px-2 py-2 border-r border-emerald-600"><SortHeader field="utr" sort={sort} onChange={setSort}>UTR</SortHeader></th>
                                        <th className="px-2 py-2 text-right border-r border-emerald-600"><SortHeader field="recd" align="right" sort={sort} onChange={setSort}>Recd</SortHeader></th>
                                        <th className="px-1.5 py-2 text-right border-r border-emerald-600">%</th>
                                        <th className="px-1.5 py-2 text-right border-r border-emerald-600"><SortHeader field="pctAmt" align="right" sort={sort} onChange={setSort}>% Amt</SortHeader></th>
                                        <th className="px-2 py-2 text-right border-r border-emerald-600"><SortHeader field="netAmt" align="right" sort={sort} onChange={setSort}>Net</SortHeader></th>
                                        <th className="px-2 py-2 border-r border-emerald-600 whitespace-nowrap"><SortHeader field="fixAmt" align="right" sort={sort} onChange={setSort}>Fix</SortHeader></th>
                                        <th className="px-2 py-2 text-right border-r border-emerald-600"><SortHeader field="paid" align="right" sort={sort} onChange={setSort}>Paid</SortHeader></th>
                                        <th className="px-2 py-2 text-right border-r border-emerald-600"><SortHeader field="balance" align="right" sort={sort} onChange={setSort}>Balance</SortHeader></th>
                                        <th className="px-2 py-2 border-r border-emerald-600">Remark</th>
                                        <th className="px-2 py-2 text-center w-10" aria-label="Edit"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayRows.length === 0 ? (
                                        <tr><td colSpan={11 + (moveMode ? 1 : 0) + (reconcileMode ? 1 : 0)} className="px-5 py-10 text-center text-slate-400">No transactions in range</td></tr>
                                    ) : (() => {
                                        let visible = sortRows(displayRows, sort)
                                            .filter((row) => {
                                                if (showOnlyRecent && !row._isRecent) return false;
                                                if (suspenseOnly && !row.is_suspense) return false;
                                                if (shouldHideUncheckedRow(row)) return false;
                                                if (!matchesMode(row)) return false;
                                                if (rowSearch.trim()) {
                                                    const q = rowSearch.trim().toLowerCase();
                                                    if (!rowHaystack(row).includes(q)) return false;
                                                }
                                                return true;
                                            });
                                        // Stash visible ids on a ref-ish global so the header
                                        // Select-All handler can act on the currently-visible set only.
                                        window.__reportsVisibleIds = visible.filter((r) => r._type === "payment" || r._type === "payout").map((r) => r.id);
                                        // v1.29.76 — Opening balance + month-boundary marker rows.
                                        // Only shown in a pure chronological, unfiltered view so the
                                        // "closing = opening" boundaries stay accurate.
                                        const mKey = (d) => (d || "").slice(0, 7);
                                        const mName = (mk) => { if (!mk) return ""; const [y, m] = mk.split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" }); };
                                        const lastBalByMonth = {};
                                        for (const r of displayRows) lastBalByMonth[mKey(r.date)] = r.balance;
                                        const sortedByDate = !sort.by || sort.by === "date"; // cleared sort = natural chronological (asc)
                                        const showLedgerMarkers = sortedByDate && !rowSearch.trim() && modeFilter === "ALL" && !showOnlyRecent && !suspenseOnly && !reconcileMode;
                                        const chronoAsc = !sort.by ? true : sort.dir === "asc";
                                        // v1.31.71 — Within a month, always render the mediator-commission
                                        // row LAST (asc) / FIRST (desc) so it matches the backend's
                                        // month-closing order and the balance column reads correctly.
                                        if (sortedByDate) {
                                            const order = [];
                                            const byMonth = new Map();
                                            for (const r of visible) {
                                                const mk = (r.date || "").slice(0, 7);
                                                if (!byMonth.has(mk)) { byMonth.set(mk, []); order.push(mk); }
                                                byMonth.get(mk).push(r);
                                            }
                                            const reordered = [];
                                            for (const mk of order) {
                                                const grp = byMonth.get(mk);
                                                const comm = grp.filter((r) => r._type === "mediator_commission");
                                                const rest = grp.filter((r) => r._type !== "mediator_commission");
                                                reordered.push(...(chronoAsc ? [...rest, ...comm] : [...comm, ...rest]));
                                            }
                                            visible = reordered;
                                        }
                                        let renderItems = visible;
                                        if (showLedgerMarkers && visible.length > 0) {
                                            const withDiv = [];
                                            for (let i = 0; i < visible.length; i++) {
                                                const cur = visible[i];
                                                const prev = visible[i - 1];
                                                if (prev && mKey(prev.date) !== mKey(cur.date)) {
                                                    const earlier = chronoAsc ? mKey(prev.date) : mKey(cur.date);
                                                    const later = chronoAsc ? mKey(cur.date) : mKey(prev.date);
                                                    withDiv.push({ __marker: "divider", _closeMonth: earlier, _openMonth: later, balance: lastBalByMonth[earlier] ?? 0 });
                                                }
                                                withDiv.push(cur);
                                            }
                                            const openingItem = { __marker: "opening", balance: Number(ledger.opening_balance || 0) };
                                            renderItems = chronoAsc ? [openingItem, ...withDiv] : [...withDiv, openingItem];
                                        }
                                        // v1.29.87 — Reconcile-only opening/closing marker rows.
                                        // Per selected range: overall opening (before FROM), a daily
                                        // opening (= prev day's closing) before each new date except
                                        // the first, a daily closing after each date's last txn, and
                                        // an overall closing at the very end. Only when Reconcile Mode
                                        // is ON and the view is a pure chronological, unfiltered range
                                        // (so slip counts / net / balances stay accurate). Markers are
                                        // display-only — not selectable, not counted, not reconcilable.
                                        const showReconcileMarkers = chronoAsc && sortedByDate
                                            && !rowSearch.trim() && modeFilter === "ALL"
                                            && !showOnlyRecent && !suspenseOnly && !showOnlyUnchecked;
                                        if (showReconcileMarkers && visible.length > 0) {
                                            const items = [];
                                            const dates = [];
                                            const byDate = new Map();
                                            for (const r of visible) {
                                                if (!byDate.has(r.date)) { byDate.set(r.date, []); dates.push(r.date); }
                                                byDate.get(r.date).push(r);
                                            }
                                            items.push({ __rmarker: "overall-open", date: dateFrom || dates[0], balance: Number(ledger.opening_balance || 0) });
                                            let totalSlips = 0, totalNet = 0;
                                            dates.forEach((d, di) => {
                                                const dayRows = byDate.get(d);
                                                if (di > 0) {
                                                    const prevRows = byDate.get(dates[di - 1]);
                                                    items.push({ __rmarker: "daily-open", date: d, balance: prevRows[prevRows.length - 1].balance });
                                                }
                                                dayRows.forEach((r) => items.push(r));
                                                const daySlips = dayRows.filter((r) => r._type === "payment").length;
                                                const dayNet = dayRows.reduce((s, r) => s + Number(r.netAmt || 0), 0);
                                                totalSlips += daySlips; totalNet += dayNet;
                                                items.push({ __rmarker: "daily-close", date: d, balance: dayRows[dayRows.length - 1].balance, slips: daySlips, net: dayNet });
                                            });
                                            const lastDayRows = byDate.get(dates[dates.length - 1]);
                                            items.push({ __rmarker: "overall-close", date: dateTo || dates[dates.length - 1], balance: lastDayRows[lastDayRows.length - 1].balance, slips: totalSlips, net: totalNet });
                                            renderItems = items;
                                        }
                                        return renderItems.map((row, vIdx) => { // v1.29.29 — vIdx drives zebra striping
                                        if (row.__rmarker) {
                                            const isOpen = row.__rmarker === "overall-open" || row.__rmarker === "daily-open";
                                            const bg = isOpen ? "bg-slate-900" : "bg-emerald-800";
                                            let label = "";
                                            if (row.__rmarker === "overall-open") label = "OPENING BALANCE";
                                            else if (row.__rmarker === "daily-open") label = `OPENING BALANCE — ${dmY(row.date)}`;
                                            else if (row.__rmarker === "daily-close") label = `CLOSING BALANCE — ${dmY(row.date)} | ${row.slips} Slip${row.slips === 1 ? "" : "s"} | Net ${inr(row.net)}`;
                                            else label = `CLOSING BALANCE | ${row.slips} Slip${row.slips === 1 ? "" : "s"} | Net ${inr(row.net)}`;
                                            const tid = row.__rmarker === "overall-open" ? "ledger-overall-opening"
                                                : row.__rmarker === "overall-close" ? "ledger-overall-closing"
                                                : `ledger-${row.__rmarker}-${row.date}`;
                                            return (
                                                <tr key={tid} data-testid={tid} data-marker="1" className={`${bg} text-white`}>
                                                    {moveMode && <td className="px-2 py-2" />}
                                                    <td className="px-2 py-2" />
                                                    <td className="px-2 py-2 font-bold whitespace-nowrap">{dmY(row.date)}</td>
                                                    <td colSpan={7} className="px-2 py-2 font-bold uppercase tracking-wide">{label}</td>
                                                    <td className="px-2 py-2 text-right font-mono font-bold tabular-nums whitespace-nowrap">{inr(row.balance)}</td>
                                                    <td colSpan={2} className="px-2 py-2" />
                                                </tr>
                                            );
                                        }
                                        if (row.__marker) {
                                            const span = 11 + (moveMode ? 1 : 0) + (reconcileMode ? 1 : 0);
                                            if (row.__marker === "opening") {
                                                return (
                                                    <tr key="ledger-opening-marker" data-testid="ledger-opening-marker" className="border-y-2 border-slate-300 bg-slate-100">
                                                        <td colSpan={span} className="px-3 py-2">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Opening Balance</span>
                                                                <span className={`font-mono text-[13px] font-bold tabular-nums ${balanceCls(row.balance)}`}>{inr(row.balance)}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            return (
                                                <tr key={`ledger-divider-${row._closeMonth}-${row._openMonth}`} data-testid={`ledger-month-divider-${row._closeMonth}`} className="border-y border-dashed border-emerald-400 bg-emerald-50">
                                                    <td colSpan={span} className="px-3 py-1.5">
                                                        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">Closing {mName(row._closeMonth)} = Opening {mName(row._openMonth)}</span>
                                                            <span className={`font-mono text-[13px] font-bold tabular-nums ${balanceCls(row.balance)}`}>{inr(row.balance)}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                        if (row._type === "mediator_commission") {
                                            const span = 11 + (moveMode ? 1 : 0) + (reconcileMode ? 1 : 0);
                                            return (
                                                <tr key={row.id} data-testid={`ledger-commission-summary-${row.id}`} className="commission-glow-row border-y border-blue-200">
                                                    <td colSpan={span} className="px-3 py-2">
                                                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                                                            <span className="text-xs font-bold uppercase tracking-wider">{dmY(row.date)} — Amount Paid Rs {inr0(row._commReceipts)} in {row._commLabel} — Commission Amount is {inr0(row._commAmount)} — PAYABLE</span>
                                                            <span className={`font-mono text-[13px] font-bold tabular-nums ${balanceCls(row.balance)}`}>{inr(row.balance)}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                        const remarkCls = {
                                            APPROVED: "text-emerald-700 font-semibold",
                                            REJECTED: "text-rose-700 font-semibold",
                                            INVOICE: "text-slate-600",
                                            PAID: "text-violet-700 font-semibold",
                                        }[row.remark] || (row._type === "payout" ? "text-violet-700 font-semibold" : "text-slate-600");
                                        // Compact notes: "Payment received via slip (Paytm)" → "Paytm"
                                        const compactNotes = (row.notes || "").replace(/^Payment received via slip\s*/i, "").replace(/^\(|\)$/g, "");
                                        const canReconcile = row._type === "payment" || row._type === "payout";
                                        const rowCls = row.is_suspense
                                            ? "border-b border-orange-400 bg-orange-100 hover:bg-orange-200"
                                            : row._type === "mediator_commission"
                                            ? "commission-glow-row border-b border-blue-200"
                                            : row.ocr_mismatch
                                            ? "border-b border-rose-400 bg-rose-100 hover:bg-rose-200"
                                            : reconcileMode && row.reconciled
                                                ? "border-b border-emerald-500 bg-emerald-200 hover:bg-emerald-300"
                                                : row._isRecent
                                                    ? "border-b border-slate-100 border-l-4 !border-l-amber-400 bg-amber-50/50 hover:bg-amber-100/50"
                                                    : `border-b border-slate-100 ${vIdx % 2 === 1 ? "bg-slate-50/70" : "bg-white"} hover:bg-slate-100`; // v1.29.29 — zebra striping on plain rows; semantic colours (suspense/ocr/reconciled/recent) untouched
                                        return (
                                            <tr key={row.id} className={`${rowCls} transition-opacity duration-700 ease-out ${fadingRows.has(row.id) ? "opacity-0" : "opacity-100"}`} data-testid={`ledger-row-${row.id}`} data-fading={fadingRows.has(row.id) ? "1" : "0"} data-selected={selectedIds.has(row.id) ? "true" : "false"} data-reconciled={row.reconciled ? "1" : "0"} data-ocr-mismatch={row.ocr_mismatch ? "1" : "0"} title={row.ocr_mismatch ? `OCR mismatch on: ${(row.ocr_mismatch_fields || []).join(", ")} — open the slip to fix` : undefined}>
                                                {/* v1.29.23 — Row-selection checkbox. Only shown on
                                                    payment/payout rows (matches the move-target scope). */}
                                                {moveMode && (
                                                <td className="px-2 py-1.5 text-center align-top" onClick={(e) => e.stopPropagation()}>
                                                    {(row._type === "payment" || row._type === "payout") ? (
                                                        <input
                                                            type="checkbox"
                                                            data-testid={`ledger-select-${row.id}`}
                                                            checked={selectedIds.has(row.id)}
                                                            onChange={() => setSelectedIds((prev) => {
                                                                const next = new Set(prev);
                                                                if (next.has(row.id)) next.delete(row.id);
                                                                else next.add(row.id);
                                                                return next;
                                                            })}
                                                            className="h-4 w-4 cursor-pointer"
                                                        />
                                                    ) : (
                                                        <span className="text-slate-300 text-xs">—</span>
                                                    )}
                                                </td>
                                                )}
                                                {reconcileMode && (
                                                    <td className="px-2 py-1.5 text-center">
                                                        {canReconcile ? (
                                                            <input
                                                                type="checkbox"
                                                                data-testid={`ledger-reconcile-${row.id}`}
                                                                checked={!!row.reconciled}
                                                                onChange={() => toggleReconcile(row)}
                                                                title={row.reconciled ? `Checked by ${row.reconciled_by || "?"} · click to uncheck` : "Mark as reviewed"}
                                                                className="h-4 w-4 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                                            />
                                                        ) : (
                                                            <span className="text-slate-300 text-xs">—</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="px-2 py-1.5 text-slate-800 align-top">
                                                    <TxnDateCell
                                                        date={row.date}
                                                        created_at={row.created_at}
                                                        updated_at={row.updated_at}
                                                        testId={`ledger-date-${row.id}`}
                                                        extra={row._isRecent ? (
                                                            <span
                                                                className="ml-1.5 inline-flex items-center rounded-full bg-amber-500 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-white shadow-sm"
                                                                title={row._updatedAt ? `Updated ${dmYhm(row._updatedAt)}` : "Updated recently"}
                                                                data-testid={`ledger-recent-badge-${row.id}`}
                                                            >
                                                                NEW
                                                            </span>
                                                        ) : null}
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5 font-mono font-semibold text-slate-800 max-w-[180px] truncate" title={row._isTransfer ? "Balance Transfer" : row._type === "payout" ? (row.payment_mode || row.utr) : (row.utr || "Cash")}>
                                                    {row._isTransfer
                                                        ? <span className="rounded bg-indigo-100 px-1.5 py-0.5 uppercase text-indigo-800" data-testid={`ledger-transfer-label-${row.id}`}>BALANCE&nbsp;TRANSFER</span>
                                                        : row._type === "payout"
                                                        ? (row.payment_mode
                                                            // v1.27.42 — Uniform UTR column: chip text now inherits the
                                                            // table font size (`text-base sm:text-lg`) so a "CASH PAID"
                                                            // chip sits at the exact same size and baseline as a raw
                                                            // UTR number in the row above. Uppercase + coloured bg is
                                                            // preserved for scannability.
                                                            ? <span className="rounded bg-violet-100 px-1.5 py-0.5 uppercase text-violet-800" data-testid={`ledger-payout-mode-${row.id}`}>{row.payment_mode}</span>
                                                            : (row.utr || <span className="rounded bg-violet-100 px-1.5 py-0.5 uppercase text-violet-800">CASH&nbsp;PAID</span>))
                                                        : (row.payment_mode
                                                            // v1.27.22 — For a received row (payment), if a
                                                            // payment_mode is set (UPI / PhonePe / portal name /
                                                            // Distributor A / etc), prefer it over UTR so a Rectify
                                                            // edit is actually visible on the ledger. Cash-labelled
                                                            // modes fall through to the amber CASH chip.
                                                            && row.payment_mode.toLowerCase() !== "cash"
                                                            ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 uppercase text-emerald-800" data-testid={`ledger-recd-mode-${row.id}`}>{row.payment_mode}</span>
                                                            : (row.utr
                                                                ? row.utr
                                                                // v1.25.74 — Payment received without a UTR = cash-in-hand
                                                                // (e.g. "at office", "FROM HOME"). Show a clear amber
                                                                // "CASH" chip instead of a bare "—" so the row narrative
                                                                // reads correctly for the party.
                                                                : <span className="rounded bg-amber-100 px-1.5 py-0.5 uppercase text-amber-800" data-testid={`ledger-cash-${row.id}`}>CASH</span>))}
                                                </td>
                                                <td className="px-2 py-1.5 text-right tabular-nums text-slate-900">{inr0(row.recd)}</td>
                                                <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-700" data-testid={`ledger-pct-${row.id}`}>
                                                    {row.pct ? `${row.pct.toFixed(2)}%` : ""}
                                                </td>
                                                <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-700">{inr0(row.pctAmt)}</td>
                                                <td className="px-2 py-1.5 text-right tabular-nums font-medium text-slate-900">{inr0(row.netAmt)}</td>
                                                <td className="px-1.5 py-1.5 text-right tabular-nums text-amber-800" data-testid={`ledger-fix-${row.id}`}>{inr0(row.fixAmt)}</td>
                                                <td className="px-2 py-1.5 text-right tabular-nums text-violet-800">{inr0(row.paid)}</td>
                                                <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${balanceCls(row.balance)}`}>{inr(row.balance)}</td>
                                                <td className={`px-2 py-1.5 text-xs uppercase tracking-wide ${remarkCls}`} data-testid={`ledger-remark-${row.id}`}>
                                                    <div className="whitespace-nowrap flex items-center gap-1.5">
                                                        {row.is_suspense ? (() => {
                                                            // v1.28.26 — Age-based colour + audit tooltip.
                                                            const suspTs = row.suspense_at ? new Date(row.suspense_at).getTime() : 0;
                                                            const ageDays = suspTs ? Math.floor((Date.now() - suspTs) / 86400000) : 0;
                                                            const stale = ageDays > 30;
                                                            const badgeCls = stale
                                                                ? "bg-red-700 text-white ring-2 ring-red-300"
                                                                : "bg-orange-500 text-white";
                                                            const auditTitle = `Suspended ${row.suspense_by ? `by ${row.suspense_by}` : ""}${row.suspense_at ? ` on ${new Date(row.suspense_at).toLocaleString("en-IN")}` : ""}${ageDays ? ` · ${ageDays} day${ageDays === 1 ? "" : "s"} ago` : ""}`.trim();
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="checkbox"
                                                                        data-testid={`ledger-suspense-select-${row.id}`}
                                                                        checked={selectedSuspense.has(row.id)}
                                                                        onChange={() => toggleSuspenseSelect(row.id)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-amber-600"
                                                                        title="Select for bulk un-suspense"
                                                                    />
                                                                    <span
                                                                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black tracking-wider shadow-sm ${badgeCls}`}
                                                                        data-testid={`ledger-suspense-badge-${row.id}`}
                                                                        title={auditTitle}
                                                                    >
                                                                        SUSPENSE{stale ? ` · ${ageDays}d` : ""}
                                                                    </span>
                                                                </>
                                                            );
                                                        })() : row.remark}
                                                        {!row.is_suspense && row.reconciled && (
                                                            <span
                                                                data-testid={`ledger-reconciled-badge-${row.id}`}
                                                                title={`Reconciled${row.reconciled_by ? ` by ${row.reconciled_by}` : ""}`}
                                                                className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-sm"
                                                            >
                                                                ✓ Reconciled
                                                            </span>
                                                        )}
                                                    </div>
                                                    {row.is_suspense && row.suspense_reason && (
                                                        <div className="mt-0.5 text-[11px] normal-case tracking-normal font-normal text-amber-800" data-testid={`ledger-suspense-reason-${row.id}`} title={row.suspense_reason}>
                                                            {row.suspense_reason}
                                                        </div>
                                                    )}
                                                    {compactNotes && !row.is_suspense && (
                                                        <div className="mt-0.5 text-[11px] normal-case tracking-normal font-normal text-slate-500 max-w-[140px] truncate" title={row.notes}>
                                                            {compactNotes}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-1 py-1.5 text-center">
                                                    <div className="flex items-center justify-center gap-0.5">
                                                        {(row._type === "payment" || row._type === "payout") && (
                                                            row.upload_id ? (
                                                                <button
                                                                    type="button"
                                                                    data-testid={`ledger-view-slip-${row.id}`}
                                                                    onClick={() => openSlipEdit(row.utr, row.upload_id)}
                                                                    title="Open slip verification"
                                                                    className="rounded p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-700"
                                                                >
                                                                    <Clock size={13} />
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    data-testid={`ledger-view-slip-${row.id}`}
                                                                    onClick={() => {}}
                                                                    title="No slip attached"
                                                                    className="rounded p-1.5 text-slate-300 cursor-not-allowed"
                                                                >
                                                                    <Clock size={13} />
                                                                </button>
                                                            )
                                                        )}
                                                        {(row._type === "payment" || row._type === "payout") && !row.upload_id && (
                                                            <button
                                                                type="button"
                                                                data-testid={`ledger-edit-txn-${row.id}`}
                                                                onClick={() => openEditTxn(row)}
                                                                title="Rectify this payment"
                                                                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-700"
                                                            >
                                                                <PencilSimple size={13} weight="bold" />
                                                            </button>
                                                        )}
                                                        {row._isTransfer && (
                                                            <button
                                                                type="button"
                                                                data-testid={`ledger-edit-transfer-${row.id}`}
                                                                onClick={() => openEditTransfer(row)}
                                                                title="Edit this balance transfer — updates both parties"
                                                                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-700"
                                                            >
                                                                <PencilSimple size={13} weight="bold" />
                                                            </button>
                                                        )}
                                                        {row._isTransfer && canDelete && (
                                                            <button
                                                                type="button"
                                                                data-testid={`ledger-delete-transfer-${row.id}`}
                                                                onClick={() => deleteTransfer(row)}
                                                                title="Delete this balance transfer from both parties"
                                                                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                                                            >
                                                                <Trash size={13} weight="bold" />
                                                            </button>
                                                        )}
                                                        {row.is_suspense && (
                                                            <button
                                                                type="button"
                                                                data-testid={`ledger-unsuspense-${row.id}`}
                                                                onClick={() => unsuspenseFromLedger(row)}
                                                                title="Un-suspense — restore this payment to the closing balance and re-link commission"
                                                                className="rounded p-1.5 text-amber-700 transition-colors hover:bg-amber-100 hover:text-amber-900"
                                                            >
                                                                <ArrowsCounterClockwise size={13} weight="bold" />
                                                            </button>
                                                        )}
                                                        {(row._type === "payment" || row._type === "payout") && reconcileMode && !row.reconciled && !row.is_suspense && (
                                                            <button
                                                                type="button"
                                                                data-testid={`ledger-suspense-${row.id}`}
                                                                onClick={() => moveToSuspense(row)}
                                                                title="Move to suspense — excluded from closing balance until reassigned"
                                                                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-amber-100 hover:text-amber-800"
                                                            >
                                                                <Warning size={13} weight="bold" />
                                                            </button>
                                                        )}
                                                        {(row._type === "payment" || row._type === "payout") && canDelete && (
                                                            <button
                                                                type="button"
                                                                data-testid={`ledger-move-${row.id}`}
                                                                onClick={() => setMoveTxn(row)}
                                                                title="Move this entry to another party"
                                                                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-700"
                                                            >
                                                                <ArrowsLeftRight size={13} weight="bold" />
                                                            </button>
                                                        )}
                                                        {(row._type === "payment" || row._type === "payout") && canDelete && (
                                                            <button
                                                                type="button"
                                                                data-testid={`ledger-delete-${row.id}`}
                                                                onClick={() => deleteTxn(row)}
                                                                title="Delete this transaction"
                                                                className="rounded p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                                                            >
                                                                <Trash size={13} weight="bold" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    });
                                    })()}
                                    {displayRows.length > 0 && (
                                        <tr data-testid="party-ledger-total-row" className="border-t-2 border-emerald-700 bg-slate-50 font-semibold">
                                            {moveMode && <td />}
                                            {reconcileMode && <td className="px-2 py-2" />}
                                            <td colSpan={2} className="px-2 py-2 text-slate-700 uppercase tracking-wider text-xs">Totals · {viewRows.length} rows</td>
                                            <td className="px-2 py-2 text-right tabular-nums text-slate-900" data-testid="party-ledger-total-received">
                                                {inr0(viewRows.reduce((s, r) => s + r.recd, 0))}
                                            </td>
                                            <td />
                                            <td className="px-1.5 py-2 text-right tabular-nums text-slate-900">
                                                {inr0(viewRows.reduce((s, r) => s + r.pctAmt, 0))}
                                            </td>
                                            <td className="px-2 py-2 text-right tabular-nums text-slate-900" data-testid="party-ledger-total-net">
                                                {inr0(viewRows.reduce((s, r) => s + r.netAmt, 0))}
                                            </td>
                                            <td className="px-1.5 py-2 text-right tabular-nums text-amber-800">
                                                {inr0(viewRows.reduce((s, r) => s + (r.fixAmt || 0), 0))}
                                            </td>
                                            <td className="px-2 py-2 text-right tabular-nums text-rose-700" data-testid="party-ledger-total-paid">
                                                {inr0(viewRows.reduce((s, r) => s + r.paid, 0))}
                                            </td>
                                            <td className={`px-2 py-2 text-right tabular-nums font-semibold ${balanceCls(displayRows[displayRows.length - 1].balance)}`}>
                                                {inr(displayRows[displayRows.length - 1].balance)}
                                            </td>
                                            <td />
                                            <td />
                                        </tr>
                                    )}
                                    {/* v1.29.19 — Search subtotal row. Only appears when the
                                        UTR/Remark search is active AND at least one row matches.
                                        Sums Recd / Paid / Net across the filtered set so admins
                                        can reconcile "Withdraw Goodluck this month" in one glance
                                        without exporting. Amber tint distinguishes it from the
                                        main Totals row above. */}
                                    {rowSearch.trim() && displayRows.length > 0 && (() => {
                                        const q = rowSearch.trim().toLowerCase();
                                        const matches = displayRows.filter((row) => {
                                            if (showOnlyRecent && !row._isRecent) return false;
                                            if (suspenseOnly && !row.is_suspense) return false;
                                            if (shouldHideUncheckedRow(row)) return false;
                                            return rowHaystack(row).includes(q);
                                        });
                                        if (matches.length === 0) return null;
                                        const totalRecd = matches.reduce((s, r) => s + (r.recd || 0), 0);
                                        const totalPaid = matches.reduce((s, r) => s + (r.paid || 0), 0);
                                        const totalNet = matches.reduce((s, r) => s + (r.netAmt || 0), 0);
                                        return (
                                            <tr data-testid="party-ledger-search-subtotal-row" className="border-t border-amber-300 bg-amber-50 font-semibold">
                                                {moveMode && <td />}
                                                {reconcileMode && <td className="px-2 py-2" />}
                                                <td colSpan={2} className="px-2 py-2 text-amber-900 uppercase tracking-wider text-xs">
                                                    Search "{rowSearch.trim()}" · {matches.length} row{matches.length === 1 ? "" : "s"}
                                                </td>
                                                <td className="px-2 py-2 text-right tabular-nums text-emerald-800" data-testid="party-ledger-search-total-received">
                                                    {inr0(totalRecd)}
                                                </td>
                                                <td />
                                                <td />
                                                <td className="px-2 py-2 text-right tabular-nums text-slate-900" data-testid="party-ledger-search-total-net">
                                                    {inr0(totalNet)}
                                                </td>
                                                <td />
                                                <td className="px-2 py-2 text-right tabular-nums text-rose-700" data-testid="party-ledger-search-total-paid">
                                                    {inr0(totalPaid)}
                                                </td>
                                                <td />
                                                <td />
                                                <td />
                                            </tr>
                                        );
                                    })()}
                                    {/* v1.29.48 — In-table "PENDING SLIPS — YET TO
                                        VERIFY" block (amber divider + per-slip rows)
                                        removed. Pending slips now live in the floating
                                        pending-slips icon + review panel. The
                                        pendingSlipRows figures still feed the bottom-bar
                                        grand totals and the floating panel. */}
                                </tbody>
                            </table>
                        </div>
                        <div ref={tableBottomRef} data-testid="ledger-bottom-marker" aria-hidden />
                    </div>
                </div>
            )}
            </div>{/* end MIDDLE scroll zone */}

            {/* v1.29.22 — Move Entry dialog. Renders at the page root so it
                overlays cleanly regardless of table scroll position. */}
            {moveTxn && (
                <MoveEntryDialog
                    entry={moveTxn}
                    parties={parties}
                    currentPartyId={partyId}
                    onClose={() => setMoveTxn(null)}
                    onDone={() => { setMoveTxn(null); reloadLedger(); }}
                />
            )}

            {/* v1.29.36 — Old bottom bulk-action bar + bulk-move modal removed:
                bulk move now lives in the inline toolbar above the table
                (ledger-bulk-move-bar). Single-entry move (MoveEntryDialog via
                the per-row icon above) is unchanged. */}

            {/* v1.29.13 — Floating "Latest" jump button. Scrolls the viewport
                to whichever end currently holds the newest entry (top for
                newest-first, bottom for oldest-first). Only surfaces once
                the table has >= 10 rows AND the user has scrolled away
                from that end — keeps the UI quiet on small ledgers. */}
            <JumpToLatestButton
                direction={sort?.dir === "asc" ? "asc" : "desc"}
                rowCount={displayRows.length}
                containerSelector='[data-testid="ledger-scroll-zone"]'
                bottomOffsetClass="bottom-56 sm:bottom-28"
                onJump={() => {
                    const target = (sort?.dir === "asc" ? tableBottomRef : tableTopRef).current;
                    target?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                testId="ledger-jump-latest"
            />

            {/* Rectify payment / payout dialog */}
            <Dialog open={!!editTxn} onOpenChange={(o) => !o && setEditTxn(null)}>
                <DialogContent className="max-w-md" data-testid="edit-txn-dialog">
                    <DialogHeader>
                        <DialogTitle>Rectify {editTxn?._type === "payout" ? "Payment Made" : "Payment Received"}</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Edit the amount, date or mode. Linked commission and portal charges will be recomputed automatically, or you can override them manually below.
                        </DialogDescription>
                    </DialogHeader>
                    {editTxn && (
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs text-slate-500">Amount (₹)</Label>
                                <AmountInput value={editTxn.amount} onChange={(e) => setEditTxn({ ...editTxn, amount: e.target.value })} data-testid="edit-txn-amount" className="mt-1" wordsClassName="!text-[13px] !font-bold !text-gray-900" />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Date</Label>
                                <Input type="date" value={editTxn.date || ""} onChange={(e) => setEditTxn({ ...editTxn, date: e.target.value })} data-testid="edit-txn-date" className="mt-1" />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Payment Mode</Label>
                                <Select value={editTxn.payment_mode || ""} onValueChange={onRectifyModeChange}>
                                    <SelectTrigger data-testid="edit-txn-mode" className="mt-1 bg-white">
                                        <SelectValue placeholder={paymentModes.length ? "Pick a mode" : "No modes yet — add below"} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72">
                                        {paymentModes.length === 0 && (
                                            <SelectItem value="Cash">Cash</SelectItem>
                                        )}
                                        {sortedPaymentModes.map((m) => (
                                            <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {/* v1.27.20 — inline "add new mode" so admins don't
                                    have to leave the dialog to create UPI, portal,
                                    or distributor names on the fly. */}
                                <div className="mt-2 flex items-center gap-2">
                                    <Input
                                        data-testid="edit-txn-add-mode-input"
                                        value={newModeName}
                                        onChange={(e) => setNewModeName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPaymentMode(); } }}
                                        placeholder="Add new · e.g. PhonePe, Distributor A"
                                        className="h-8 bg-white text-sm"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        data-testid="edit-txn-add-mode-btn"
                                        onClick={addPaymentMode}
                                        disabled={!newModeName.trim()}
                                        className="h-8 shrink-0 border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"
                                    >
                                        + Add
                                    </Button>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Remarks</Label>
                                <Input value={editTxn.description || ""} onChange={(e) => setEditTxn({ ...editTxn, description: e.target.value })} data-testid="edit-txn-notes" className="mt-1" />
                            </div>
                            {/* v1.29.03 — Manual commission + portal-fee overrides.
                                Only shown when rectifying a Payment Received
                                (payouts have no linked commission by design). The
                                two-way sync keeps % ↔ amount in step; the
                                _commTouched / _feeTouched flags gate whether
                                the payload actually sends these fields (so a
                                reopen-without-edit still falls through to the
                                pricing-rule recompute path). */}
                            {editTxn._type !== "payout" && (
                                <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="edit-txn-commission-panel">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Commission (override)</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <Label className="text-[10px] text-slate-500">Commission %</Label>
                                            <Input
                                                data-testid="edit-txn-comm-pct"
                                                type="number" step="0.01" min="0"
                                                value={editTxn.commission_percent ?? ""}
                                                onChange={(e) => {
                                                    const pct = e.target.value;
                                                    const amt = pct !== "" ? Number((Number(editTxn.amount || 0) * Number(pct) / 100).toFixed(2)) : "";
                                                    setEditTxn({ ...editTxn, commission_percent: pct, commission_amount: amt, _commTouched: true });
                                                }}
                                                className="mt-1 h-9"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-[10px] text-slate-500">Commission Amount (₹)</Label>
                                            <Input
                                                data-testid="edit-txn-comm-amt"
                                                type="number" step="0.01" min="0"
                                                value={editTxn.commission_amount ?? ""}
                                                onChange={(e) => {
                                                    const amt = e.target.value;
                                                    const base = Number(editTxn.amount || 0);
                                                    const pct = amt !== "" && base > 0 ? Number((Number(amt) * 100 / base).toFixed(4)) : "";
                                                    setEditTxn({ ...editTxn, commission_amount: amt, commission_percent: pct, _commTouched: true });
                                                }}
                                                className="mt-1 h-9"
                                            />
                                        </div>
                                    </div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Portal Fee (override)</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <Label className="text-[10px] text-slate-500">Portal Fee %</Label>
                                            <Input
                                                data-testid="edit-txn-fee-pct"
                                                type="number" step="0.01" min="0"
                                                value={editTxn.portal_fee_percent ?? ""}
                                                onChange={(e) => {
                                                    const pct = e.target.value;
                                                    const amt = pct !== "" ? Number((Number(editTxn.amount || 0) * Number(pct) / 100).toFixed(2)) : "";
                                                    setEditTxn({ ...editTxn, portal_fee_percent: pct, portal_fee_amount: amt, _feeTouched: true });
                                                }}
                                                className="mt-1 h-9"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-[10px] text-slate-500">Portal Fee Amount (₹)</Label>
                                            <Input
                                                data-testid="edit-txn-fee-amt"
                                                type="number" step="0.01" min="0"
                                                value={editTxn.portal_fee_amount ?? ""}
                                                onChange={(e) => {
                                                    const amt = e.target.value;
                                                    const base = Number(editTxn.amount || 0);
                                                    const pct = amt !== "" && base > 0 ? Number((Number(amt) * 100 / base).toFixed(4)) : "";
                                                    setEditTxn({ ...editTxn, portal_fee_amount: amt, portal_fee_percent: pct, _feeTouched: true });
                                                }}
                                                className="mt-1 h-9"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-slate-500 italic">
                                        Manual overrides apply to THIS transaction only — the party's default commission rate stays unchanged.
                                    </p>
                                </div>
                            )}
                            {/* Move to another party — mainly for wrongly-parked payouts. */}
                            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
                                <Label className="text-xs font-semibold uppercase tracking-wider text-amber-800">Move to another party</Label>
                                <p className="mt-1 text-[11px] text-amber-700">
                                    Reassigns this {editTxn._type === "payout" ? "payment made" : "payment received"} to a different party. Linked commission &amp; portal fees follow automatically.
                                </p>
                                <Select value={editTxn.party_id || ""} onValueChange={(v) => setEditTxn({ ...editTxn, party_id: v })}>
                                    <SelectTrigger data-testid="edit-txn-party" className="mt-2 bg-white"><SelectValue placeholder="Select party" /></SelectTrigger>
                                    <SelectContent>
                                        {parties.filter((p) => p.active !== false).map((p) => (
                                            <SelectItem key={p.id} value={p.id}>
                                                {p.name}{p.id === editTxn.original_party_id ? "  · current" : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {editTxn.party_id && editTxn.party_id !== editTxn.original_party_id && (
                                    <div className="mt-2 rounded bg-amber-100 px-2 py-1.5 text-[11px] font-semibold text-amber-900" data-testid="edit-txn-move-warning">
                                        ⚠ Saving will move this row out of the current party's ledger.
                                    </div>
                                )}
                            </div>
                            {editTxnError && (
                                <div data-testid="edit-txn-error-inline" role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{editTxnError}</div>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setEditTxn(null)} className="rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
                                <Button data-testid="edit-txn-save" onClick={saveTxn} className="bg-emerald-600 text-white hover:bg-emerald-700">
                                    <FloppyDisk size={14} className="mr-2" /> Save changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* v1.29.43 — Edit Balance Transfer dialog. Amount / date / remark
                only; the UTR label 'BALANCE TRANSFER' is fixed. Saving updates
                both parties (Paid on source, Recd on destination) together. */}
            <Dialog open={!!editTransfer} onOpenChange={(o) => !o && setEditTransfer(null)}>
                <DialogContent className="max-w-md" data-testid="edit-transfer-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit Balance Transfer</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Changes apply to BOTH parties at once — Paid on the source and Recd on the destination. The UTR label stays “BALANCE TRANSFER”.
                        </DialogDescription>
                    </DialogHeader>
                    {editTransfer && (
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs text-slate-500">UTR</Label>
                                <div className="mt-1 inline-flex rounded bg-indigo-100 px-2 py-1 text-xs font-bold uppercase text-indigo-800" data-testid="edit-transfer-utr-fixed">BALANCE TRANSFER</div>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Amount (₹)</Label>
                                <AmountInput value={editTransfer.amount} onChange={(e) => setEditTransfer({ ...editTransfer, amount: e.target.value })} data-testid="edit-transfer-amount" className="mt-1" wordsClassName="!text-[13px] !font-bold !text-gray-900" />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Date</Label>
                                <Input type="date" value={editTransfer.date || ""} onChange={(e) => setEditTransfer({ ...editTransfer, date: e.target.value })} data-testid="edit-transfer-date" className="mt-1" />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Remark</Label>
                                <Input value={editTransfer.remarks || ""} onChange={(e) => setEditTransfer({ ...editTransfer, remarks: e.target.value })} data-testid="edit-transfer-remark" className="mt-1" />
                            </div>
                            {editTransferError && (
                                <div data-testid="edit-transfer-error-inline" role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{editTransferError}</div>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setEditTransfer(null)} className="rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
                                <Button data-testid="edit-transfer-save" onClick={saveTransfer} className="bg-emerald-600 text-white hover:bg-emerald-700">
                                    <FloppyDisk size={14} className="mr-2" /> Save changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* v1.29.31 — Last-10-Updates dialog removed: the button now
                toggles the yellow "Last 24 hours" dropdown panel below the top
                bar instead of opening a modal. */}

            {/* v1.25.22 — Floating summary bar: Opening / Received / Paid /
                Closing. v1.29.29 — now the fixed BOTTOM ZONE of the three-zone
                layout (in-flow flex child, no longer position:fixed); content
                and styling unchanged. */}
            {ledger && partyId && (
                <div
                    data-testid="floating-summary-bar"
                    className="shrink-0 border-t-2 border-slate-950 bg-slate-900 px-3 py-3 text-white shadow-2xl sm:px-6"
                >
                    <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-2 text-sm sm:grid-cols-4 sm:gap-4">
                        {/* v1.29.35 — Every figure shows three lines: VERIFIED TOTAL
                            (posted entries only, green label) · PENDING APPROVAL
                            (unverified slips, amber label) · GRAND TOTAL IF ALL
                            APPROVED (white/bold). All derive from live state, so the
                            grand totals update the instant a slip is approved. */}
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-400">Opening Balance</div>
                            <div className="mt-0.5 space-y-0.5">
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Verified total</span>
                                    <span data-testid="fb-opening" className="font-mono text-[13px] font-bold tabular-nums">{fmt(ledger.opening_balance)}</span>
                                </div>
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">Pending approval</span>
                                    <span data-testid="fb-opening-pending" className="font-mono text-[12px] font-bold tabular-nums text-amber-200">—</span>
                                </div>
                                <div className="flex items-baseline justify-between gap-2 border-t border-slate-700 pt-0.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">Grand total (if all approved)</span>
                                    <span data-testid="fb-opening-grand" className="font-mono text-[14px] font-black tabular-nums text-white">{fmt(ledger.opening_balance)}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-emerald-300">Total Received</div>
                            <div className="mt-0.5 space-y-0.5">
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Verified total</span>
                                    <span data-testid="fb-received" className="font-mono text-[13px] font-bold tabular-nums text-emerald-200">{fmt(verifiedReceived)}</span>
                                </div>
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">Pending approval</span>
                                    <span data-testid="fb-received-pending" className="font-mono text-[12px] font-bold tabular-nums text-amber-200">{pendingSlipSum > 0 ? `+${fmt(pendingSlipSum)}` : "—"}</span>
                                </div>
                                <div className="flex items-baseline justify-between gap-2 border-t border-slate-700 pt-0.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">Grand total (if all approved)</span>
                                    <span data-testid="fb-received-grand" className="font-mono text-[14px] font-black tabular-nums text-white">{fmt(verifiedReceived + pendingSlipSum)}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-rose-300">Total Paid</div>
                            <div className="mt-0.5 space-y-0.5">
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Verified total</span>
                                    <span data-testid="fb-paid" className="font-mono text-[13px] font-bold tabular-nums text-rose-200">{fmt(verifiedPaid)}</span>
                                </div>
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">Pending approval</span>
                                    <span data-testid="fb-paid-pending" className="font-mono text-[12px] font-bold tabular-nums text-amber-200">—</span>
                                </div>
                                <div className="flex items-baseline justify-between gap-2 border-t border-slate-700 pt-0.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">Grand total (if all approved)</span>
                                    <span data-testid="fb-paid-grand" className="font-mono text-[14px] font-black tabular-nums text-white">{fmt(verifiedPaid)}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-amber-300">Closing Balance</div>
                            <div className="mt-0.5 space-y-0.5">
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Verified total</span>
                                    <span data-testid="fb-closing" className={`font-mono text-[13px] font-bold tabular-nums ${verifiedClosing > 0 ? "text-emerald-200" : verifiedClosing < 0 ? "text-rose-200" : "text-slate-200"}`}>
                                        {fmt(ledger.closing_balance)} <span className="text-[10px] font-normal opacity-70">{balanceLabel(ledger.closing_balance)}</span>
                                    </span>
                                </div>
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">Pending approval</span>
                                    <span data-testid="fb-closing-pending" className="font-mono text-[12px] font-bold tabular-nums text-amber-200">{pendingSlipSum > 0 ? `-${fmt(pendingSlipSum)}` : "—"}</span>
                                </div>
                                <div className="flex items-baseline justify-between gap-2 border-t border-slate-700 pt-0.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">Grand total (if all approved)</span>
                                    <span data-testid="fb-closing-grand" className={`font-mono text-[14px] font-black tabular-nums ${verifiedClosing - pendingSlipSum > 0 ? "text-emerald-100" : verifiedClosing - pendingSlipSum < 0 ? "text-rose-100" : "text-white"}`}>
                                        {fmt(verifiedClosing - pendingSlipSum)} <span className="text-[10px] font-normal opacity-70">{balanceLabel(verifiedClosing - pendingSlipSum)}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* v1.29.47 — Floating pending-slips icon (middle-right, above the
                bottom sticky bar). Amber + slow pulse + count badge when there
                are pending slips; dim grey when none. Click opens a review
                panel (Popover → closes on outside click). */}
            {ledger && partyId && (() => {
                const pending = ledger.pending_uploads || [];
                const count = pending.length;
                // v1.29.49 — stacked below the notification bell (fixed at
                // right-4 / top-1/2). Bell bottom edge is at 50%+16px; this FAB
                // starts at 50%+2rem (32px) → a clean 16px gap, no overlap.
                return (
                    <div className="fixed right-4 top-[calc(50%_+_2rem)] z-40" data-testid="pending-slips-fab-wrap">
                        <TooltipProvider delayDuration={200}>
                            <Popover open={pendingPanelOpen} onOpenChange={setPendingPanelOpen}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <PopoverTrigger asChild>
                                            <button
                                                type="button"
                                                data-testid="pending-slips-fab"
                                                aria-label={count > 0 ? `${count} pending slips — click to review` : "No pending slips"}
                                                className={`relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-colors ${count > 0 ? "animate-pulse bg-amber-500 text-white ring-4 ring-amber-300/60 hover:bg-amber-600" : "bg-slate-300 text-slate-500 hover:bg-slate-400"}`}
                                            >
                                                <Clock size={24} />
                                                {count > 0 && (
                                                    <span data-testid="pending-slips-fab-badge" className="absolute -right-1 -top-1 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full border-2 border-white bg-rose-600 px-1 text-xs font-bold text-white">
                                                        {count > 99 ? "99+" : count}
                                                    </span>
                                                )}
                                            </button>
                                        </PopoverTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                        {count > 0 ? `${count} pending slip${count > 1 ? "s" : ""} — click to review` : "No pending slips"}
                                    </TooltipContent>
                                </Tooltip>
                                <PopoverContent side="left" align="center" collisionPadding={12} className="w-[calc(100vw-3rem)] max-w-[26rem] p-0" data-testid="pending-slips-panel">
                                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-amber-50 px-4 py-3">
                                        <div className="text-sm font-semibold text-amber-900" data-testid="pending-panel-title">
                                            {count} pending slip{count === 1 ? "" : "s"}
                                        </div>
                                        {count > 0 && (
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    type="button"
                                                    data-testid="pending-verify-all"
                                                    onClick={verifyAllPending}
                                                    disabled={verifyingAll || rejectingAll || !!verifyingId || !!rejectingId}
                                                    className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    {verifyingAll ? "Verifying…" : "Verify All"}
                                                </button>
                                                <button
                                                    type="button"
                                                    data-testid="pending-reject-all"
                                                    onClick={rejectAllPending}
                                                    disabled={verifyingAll || rejectingAll || !!verifyingId || !!rejectingId}
                                                    className="rounded-md bg-rose-600 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    {rejectingAll ? "Rejecting…" : "Reject All"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="max-h-80 divide-y divide-slate-100 overflow-auto">
                                        {count === 0 ? (
                                            <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="pending-panel-empty">
                                                No pending slips — all caught up.
                                            </div>
                                        ) : pending.map((u) => (
                                            <div key={u.id} data-testid={`pending-item-${u.id}`} className="px-4 py-2.5 text-sm">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-slate-700">{dmY(u.payment_date)}</span>
                                                    <span className="font-semibold tabular-nums text-slate-900">{fmt(u.amount)}</span>
                                                </div>
                                                <div className="mt-0.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                    <div className="min-w-0">
                                                        <div className="break-all font-mono text-xs text-slate-500" data-testid={`pending-utr-${u.id}`}>UTR {u.utr || "—"}</div>
                                                        <div className="text-xs text-slate-400">{u.payment_mode || u.payment_app || "—"}</div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-1.5 sm:shrink-0">
                                                        <button
                                                            type="button"
                                                            data-testid={`pending-view-${u.id}`}
                                                            onClick={() => openSlipEdit(u.utr, u.id)}
                                                            disabled={verifyingAll || rejectingAll || !!verifyingId || !!rejectingId}
                                                            title="Open the full slip verification view"
                                                            className="rounded-md bg-slate-800 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            View
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid={`pending-verify-${u.id}`}
                                                            onClick={() => verifyPendingSlip(u)}
                                                            disabled={verifyingAll || rejectingAll || !!verifyingId || !!rejectingId}
                                                            className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            {verifyingId === u.id ? "…" : "Verify"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid={`pending-reject-${u.id}`}
                                                            onClick={() => rejectPendingSlip(u)}
                                                            disabled={verifyingAll || rejectingAll || !!verifyingId || !!rejectingId}
                                                            className="rounded-md bg-rose-600 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            {rejectingId === u.id ? "…" : "Reject"}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </TooltipProvider>
                    </div>
                );
            })()}
        </div>
    );
}

function MiniStat({ label, value, tone = "slate", testId }) {
    const tones = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        rose: "text-rose-700",
    };
    return (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className={`mt-0.5 font-display text-lg font-bold tabular-nums ${tones[tone]}`}>{value}</div>
        </div>
    );
}
